import React from 'react';
import { Link } from 'react-router';
import { mapApi } from '../../requests/api';
import { writeParcelHandoff } from '../../utils/parcelHandoff';
import { dropRestOfDoubleClick } from '../../utils/dropRestOfDoubleClick';
import ParcelLayer, { PARCEL_MIN_ZOOM, wktToPaths } from './ParcelLayer';
import PinLayer, { flagPinUrl } from './PinLayer';
import TerritoryLayer from './TerritoryLayer';
import ShapeTool from './ShapeTool';
import CategoryFilter, { categoryPinFilters } from './CategoryFilter';
import SidePanel from './SidePanel';
import SummaryStep, { crmLeadHref } from './panels/SummaryStep';
import NotesWindow, { NotesIcon } from '../NotesWindow';
import AddFormStep, { fieldsClearedBy } from './panels/AddFormStep';
import UpdateStep from './panels/UpdateStep';
import SelectionStep from './panels/SelectionStep';
import LeadMatchesModal from './panels/LeadMatchesModal';
import ImportsStep from './panels/ImportsStep';
import QueueStep, { isReadyToAdd, ownerKey, reviewGroups } from './panels/QueueStep';
import LogStep from './panels/LogStep';
import {
    BoxIcon,
    CaretDownIcon,
    ChevronLeftIcon,
    CursorIcon,
    HouseIcon,
    ImportIcon,
    LayersIcon,
    ParcelLinesIcon,
    RadiusIcon,
    SearchIcon,
} from './icons';
import { scaleLabel } from './format';
import { STATUS_FLAG_TITLES } from '../ListTable/statusFlagKind';

const DEFAULT_CENTER = { lat: 33.1, lng: -96.65 };
const DEFAULT_ZOOM = 18;
const MIN_ZOOM = 4;
const MAX_ZOOM = 21;

// A territory's outline decides where the map opens, so it starts wide: at the
// default zoom the parcel layer would fetch a corner of Allen first.
const TERRITORY_START_ZOOM = 6;
const TERRITORY_FIT_PADDING_PX = 48;
const MISSING_ZIPS_LISTED = 3;

// Google fires click before dblclick, so a single click waits this long to see
// whether a second one is coming.
/**
 * No choice made yet. `kind` is set the moment the card opens — the server's
 * suggestion when it has one, the assessor's entity when it does not — and
 * `touched` stays false until a person picks, which is what keeps a held
 * property's Save button out of the way until there is something to save.
 */
const EMPTY_OWNER = { kind: null, id: null, key: null, contact: {}, fields: [], alongside: false, asNew: false, touched: false };

const DOUBLE_CLICK_GRACE_MS = 260;

// Parcels per Kore check on a bulk add, so the button can count them off; the
// server takes at most 25.
const BULK_CHECK_CHUNK = 10;

/** The reason under the window's own "Kore didn't answer", when it was something else. */
function leadCheckProblem(error) {
    return error.code === 'KORE_UNAVAILABLE' ? null : error.message;
}

/** A save that failed forgets its Kore answer, so the next one asks again rather than resend it. */
function withoutLeadDecision(decisions, key) {
    const kept = { ...decisions };

    delete kept[key];

    return kept;
}

const TOAST_MS = 6000;

// A save files a new owner before the queue has made their Kore lead. The card
// asks again until it has, for a minute at most, so the CRM link turns up.
const LEAD_POLL_MS = 3000;
const LEAD_POLL_TRIES = 20;

// A fetch that answers quickly should not flash a spinner, and one that shows a
// spinner should hold it long enough to read. Both numbers are the design's.
const SPINNER_DELAY_MS = 200;
const SPINNER_HOLD_MS = 400;

// Ceiling on the search spinner, for a term Google returns nothing for.
const SEARCH_SPINNER_MAX_MS = 2500;

// The card sits over the left of the map and the toolbar over the top, so a
// parcel fitted to the bare canvas can come to rest behind either of them.
const SEARCH_FIT_PADDING = { top: 76, right: 24, bottom: 72, left: 484 };

// Where a search lands when there is no parcel to frame instead: close enough
// to read the buildings, which is the most the address alone can offer.
const SEARCH_FALLBACK_ZOOM = 19;

// Matches closer together than this are centred at MATCH_CLOSE_ZOOM instead of
// fitted: a fit around a pair of neighbours lands at the map's maximum zoom.
// The span is about what zoom 18 shows across this view.
const MATCH_CLOSE_SPAN_DEGREES = 0.004;
const MATCH_CLOSE_ZOOM = 18;

// Summit's brand blue. A parcel wears it whatever the property's status — the
// client asked to keep the parcel lines blue — and status is the pins' to show.
// The stroke is the same hue a step more saturated: at the brand tint alone
// the boundaries washed out over bright imagery ("a bit lifeless" — client).
const SUMMIT_BLUE = '#90B9ED';
const SUMMIT_BLUE_STROKE = '#6FA3E8';

// The client's territory a few shades darker: a dark blue line round the same
// light blue. Not #1570EF, which is the territory outline and the clusters.
const TERRITORY_STROKE = '#1E40AF';

// A highlighted parcel at rest, under the pointer and inside a drawn shape.
const IN_ICLIMB_TINT = { stroke: SUMMIT_BLUE_STROKE, weight: 2, hoverWeight: 2.4, fill: 0.26, hoverFill: 0.4, shapeFill: 0.45 };
const TERRITORY_TINT = { stroke: TERRITORY_STROKE, weight: 2.5, hoverWeight: 3, fill: 0.38, hoverFill: 0.52, shapeFill: 0.52 };

// Whatever the map is pointing at: the parcel a click opened, the one a search
// landed on, and the dot a search drops when there is no parcel to land on.
const ACTIVE_MARK = '#FACC15';

// What a pin's colour means: the client's names, and the client's rule for each in short.
const FLAG_NOTES = {
    green: 'Active, listed by others',
    neon: 'Active, listed by Summit',
    red: 'Listed or bought through Summit',
    orange: 'External sale',
};
const FLAG_LEGEND = ['green', 'neon', 'red', 'orange'].map((kind) => ({ bucket: kind, label: STATUS_FLAG_TITLES[kind], note: FLAG_NOTES[kind] }));

// The list's territory filter, which Filter.js always writes as at least a ZIP list.
const TERRITORY_FILTER_KEYS = ['territory_agent', 'territory', 'territory_market', 'territory_zip'];

// ?status=Active,Closed&market=… — written by the list's Parcels button, so
// the map highlights the same properties the list was showing.
const FILTER_KEYS = [
    'status', 'source', 'market', 'category', 'type', 'state', 'zip',
    ...TERRITORY_FILTER_KEYS,
];

// Same group SummaryStep blocks adds for: the assessor's single family
// residential codes, which the parcel layer leaves undrawn by default.
const RESIDENTIAL_LAND_USE_GROUP = '10';

export default class ParcelMap extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            pins: null,
            // The outline of the territory the list was filtered by, when it was.
            territory: null,
            zoom: this.startingView().zoom,
            // For the scale bar, which Mercator makes depend on it.
            latitude: this.startingView().center.lat,
            showParcels: true,
            includeResidential: this.startingIncludeResidential(),
            isLayersOpen: false,
            // The status flags folded behind a button when the top bar is short of room.
            legendCompact: false,
            isPinKeyOpen: false,
            tool: null,
            parcelStatus: { status: 'zoom', count: 0 },
            isLoadingParcels: false,
            isSearching: false,
            hoverParcelId: null,
            hoverRowId: null,

            step: null,
            selection: null,
            isLoadingSelection: false,
            // The parcel the last search landed on. Kept apart from the
            // selection so closing the card does not take the mark with it —
            // the card is closed to look at the map, which is where the answer
            // to "which one was it" has to still be.
            searchedParcelId: null,

            form: null,
            isSaving: false,
            // The one owner choice the card asks for, whichever list it came
            // from: kind is 'true' (a LightBox contact), 'record' (the
            // assessor's entity) or 'investor' (one iCLIMB holds). `touched`
            // is what tells a held property's Save button to appear.
            owner: EMPTY_OWNER,
            // Investors the user looked up by hand, offered beside the
            // server's suggestions. Held here because the picker is unmounted
            // every time the card becomes the add form.
            foundInvestors: [],
            // The add goes in with no investor at all and the property is
            // listed for owner research.
            needOwner: false,
            isSavingOwner: false,
            // Bumped after a save so the owner list re-reads who iCLIMB
            // holds and shows the person just attached as the owner.
            ownerSavedAt: 0,

            // Kore's answer to "is this owner already a lead?", per owner a save
            // would file: { status: loading|done|error, needsCheck, matches, total, error }.
            leadChecks: {},
            // What the user answered for each: { koreLeadId } or { createNew: true }.
            leadDecisions: {},
            // A save waiting on a Kore answer still on its way: 'add' or 'saveOwner'.
            leadWaiting: null,
            // The duplicate window, single or bulk; null when closed.
            leadModal: null,
            // The notes window: whose notes, and what its heading says; null when closed.
            notes: null,
            // A bulk add under way: its parcels, Kore's answers per parcel, and
            // the user's answers for the parcels that matched.
            bulkAddRun: null,
            bulkAnswers: {},
            // How far the bulk add's Kore check has got, for its button.
            bulkCheck: null,

            diffRows: [],
            diffRejected: {},
            diffError: null,
            isLoadingDiff: false,

            shapeType: null,
            size: { widthMiles: 0.4, heightMiles: 0.25, radiusMiles: 0.25 },
            shapeResults: [],
            isLoadingShape: false,
            selTab: 'new',
            excluded: {},

            imports: [],
            isLoadingImports: false,
            importZip: '',
            isStartingImport: false,
            importError: null,
            activeImport: null,

            // The category filter's picks, keyed by PROPERTIES column, and the
            // pins they match — null while nothing is picked.
            categoryFilter: {},
            matches: null,
            isLoadingMatches: false,
            matchesError: null,

            queueRows: [],
            queueRejected: {},
            isLoadingQueue: false,
            queueTotal: 0,
            queueCursor: null,
            isLoadingMoreQueue: false,
            // The queue's Kore side: which tab is open, how many rows each tab
            // holds, the answer given per owner, and the review window.
            queueTab: 'all',
            queueCounts: null,
            queueAnswers: {},
            queueReview: null,

            toast: null,
            error: null,
        };

        this.mapNode = null;
        this.searchNode = null;
        this.map = null;
        this.pinLayer = null;
        this.parcelLayer = null;
        this.shapeTool = null;
        this.searchMarker = null;
        this.clickTimer = null;
        this.toastTimer = null;
        this.layersNode = null;
        this.onKeyDown = (event) => this.handleShortcut(event);
        // Capture phase: the menu rows stop the event from reaching the
        // document on their own click, and in the bubble phase this would fire
        // first and shut the menu before a switch could be read.
        this.onDocumentDown = (event) => {
            if (this.state.isPinKeyOpen && !(this.pinKeyNode && this.pinKeyNode.contains(event.target))) {
                this.setState({ isPinKeyOpen: false });
            }

            if (!this.state.isLayersOpen) return;
            if (this.layersNode && this.layersNode.contains(event.target)) return;

            this.setState({ isLayersOpen: false });
        };
    }

    componentDidMount() {
        if (window.google && window.google.maps) {
            this.initMap();
        } else {
            this.setState({ error: 'Google Maps failed to load.' });
        }

        // Neither of these is drawn by Google, so they load either way.
        // Returning early left the imports panel saying "No imports yet" on a
        // ZIP job that was running — an empty answer to a question it had
        // never asked.
        this.loadPins();
        this.loadImports();
        this.openLinkedProperty();
        window.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('mousedown', this.onDocumentDown, true);

        // Room changes reach the search box or the spacer, whichever is
        // giving way. Measured a frame later, so the fold's own resize is not
        // reported inside the same observation (a "ResizeObserver loop" error).
        this.topbarObserver = new ResizeObserver(() => {
            cancelAnimationFrame(this.fitLegendFrame);
            this.fitLegendFrame = requestAnimationFrame(() => this.fitLegend());
        });
        [this.topbarNode, this.searchBoxNode, this.spacerNode].filter(Boolean).forEach((node) => this.topbarObserver.observe(node));
    }

    /**
     * Every toast clears itself.
     *
     * Done here rather than at each call site because there are seventeen of
     * them and the ones that forgot were all failures — so an error stayed on
     * screen until it was dismissed by hand, while a success faded.
     */
    componentDidUpdate(prevProps, prevState) {
        if (this.state.toast && this.state.toast !== prevState.toast) this.startToastTimer();

        // A save waiting on Kore belongs to the screen it was asked from: after
        // Back or Cancel, Kore's answer still carried it out.
        if (this.state.leadWaiting && this.state.step !== prevState.step) this.setState({ leadWaiting: null });

        // A bulk add belongs to the ticks it started from: closing the panel or
        // changing them mid-check still added the old set.
        if (this.state.bulkAddRun && !this.state.leadModal && (this.state.step !== 'selection'
            || this.state.excluded !== prevState.excluded
            || this.state.shapeResults !== prevState.shapeResults)) {
            this.setState({ bulkAddRun: null, bulkCheck: null, bulkAnswers: {} });
        }

        // The review window answers for the queue on screen and nothing else.
        if (this.state.queueReview && this.state.step !== 'queue') this.setState({ queueReview: null });
    }

    componentWillUnmount() {
        clearTimeout(this.clickTimer);
        clearTimeout(this.toastTimer);
        clearTimeout(this.leadPollTimer);
        clearTimeout(this.parcelLoadTimer);
        clearTimeout(this.searchTimer);
        if (this.searchObserver) this.searchObserver.disconnect();
        window.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('mousedown', this.onDocumentDown, true);
        if (this.topbarObserver) this.topbarObserver.disconnect();
        cancelAnimationFrame(this.fitLegendFrame);
        if (this.pinLayer) this.pinLayer.destroy();
        if (this.parcelLayer) this.parcelLayer.destroy();
        if (this.shapeTool) this.shapeTool.destroy();
        if (this.territoryLayer) this.territoryLayer.destroy();
        this.clearSearchMarker();
    }

    // ---------------------------------------------------------------- map

    /** ?lat=&lng=&zoom= opens the map somewhere specific, for shared links. */
    startingView() {
        const query = (this.props.location && this.props.location.query) || {};
        const lat = parseFloat(query.lat);
        const lng = parseFloat(query.lng);
        const zoom = parseInt(query.zoom, 10);

        return {
            center: isNaN(lat) || isNaN(lng) ? DEFAULT_CENTER : { lat, lng },
            zoom: isNaN(zoom) ? this.defaultZoom() : Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom)),
        };
    }

    defaultZoom() {
        return this.territoryZips().length > 0 && !this.hasLinkedCenter() ? TERRITORY_START_ZOOM : DEFAULT_ZOOM;
    }

    hasLinkedCenter() {
        const query = (this.props.location && this.props.location.query) || {};

        return !isNaN(parseFloat(query.lat)) && !isNaN(parseFloat(query.lng));
    }

    /** The ZIPs of the territory the list was filtered by, when it was. */
    territoryZips() {
        return this.pinFilters().territory_zip || [];
    }

    /**
     * Whether houses are shown, from ?residential=1.
     *
     * In the URL rather than component state alone so that a reload, or a link
     * passed to someone else, opens the map showing what it showed when the
     * link was made. The default — off — is the whole point of the filter:
     * single family homes are around 85% of the parcels in a suburban ZIP and
     * iCLIMB is a commercial system.
     */
    startingIncludeResidential() {
        const query = (this.props.location && this.props.location.query) || {};

        return query.residential === '1' || query.residential === 'true';
    }

    toggleResidential() {
        const includeResidential = !this.state.includeResidential;

        this.setState({ includeResidential }, () => {
            if (this.parcelLayer) this.parcelLayer.setIncludeResidential(includeResidential);
            this.writeResidentialToUrl(includeResidential);
        });
    }

    /**
     * Replaces the entry rather than pushing one, so Back still leaves the map
     * instead of walking through every time the filter was flipped.
     */
    writeResidentialToUrl(includeResidential) {
        if (!window.history || !window.history.replaceState) return;

        const url = new URL(window.location.href);

        if (includeResidential) {
            url.searchParams.set('residential', '1');
        } else {
            url.searchParams.delete('residential');
        }

        window.history.replaceState(null, '', url.toString());
    }

    initMap() {
        const view = this.startingView();

        this.map = new google.maps.Map(this.mapNode, {
            center: view.center,
            zoom: view.zoom,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
            mapTypeId: google.maps.MapTypeId.HYBRID,
            // The view draws its own zoom, scale and legend, so Google's
            // controls would only duplicate them in the wrong style.
            disableDefaultUI: true,
            disableDoubleClickZoom: true,
            gestureHandling: 'greedy',
            clickableIcons: true,
        });

        this.map.addListener('idle', () => this.setState({ latitude: this.map.getCenter().lat() }));

        this.map.addListener('zoom_changed', () =>
            this.setState({ zoom: this.map.getZoom() }, () => this.syncToOutlines()));

        // A Google POI click carries a placeId, and the client wants that
        // business name to become the property name. Suppressing the default
        // stops Google's own info window opening over ours.
        this.map.addListener('click', (event) => {
            if (event.placeId) event.stop();
            this.scheduleClick(event);
        });

        this.map.addListener('dblclick', (event) => {
            clearTimeout(this.clickTimer);
            if (event.placeId) event.stop();
            this.quickAddAt(event.latLng.lat(), event.latLng.lng(), event.placeId);
        });

        this.pinLayer = new PinLayer(this.map, {
            onSelect: (pin) => this.openProperty(pin.id, pin),
            onSelectMatch: (pin) => this.zoomOntoMatch(pin),
            onSelectMatches: (pins) => this.frameMatches(pins),
        });

        this.parcelLayer = new ParcelLayer(this.map, {
            includeResidential: this.state.includeResidential,
            onStatus: (parcelStatus) => this.setState({ parcelStatus }, () => {
                this.trackParcelLoading(parcelStatus.status === 'loading');
                this.restyleParcels();
                this.syncToOutlines();
            }),
            onHover: (hoverParcelId) => this.setHover('hoverParcelId', hoverParcelId),
            onSelect: (latLng) => this.scheduleClick({ latLng }),
            onQuickAdd: (latLng) => {
                clearTimeout(this.clickTimer);
                this.quickAddAt(latLng.lat(), latLng.lng(), null);
            },
        });

        this.shapeTool = new ShapeTool(this.map, {
            onChange: (measured) => this.onShapeChange(measured),
            onCommit: () => this.searchShape(),
        });

        this.territoryLayer = new TerritoryLayer(this.map);

        this.initSearch();
        this.restyleParcels();
        this.loadTerritory();
    }

    /**
     * Draws the territory the list was filtered by and, unless the link named a
     * place of its own, opens the map on the middle of it.
     */
    loadTerritory() {
        const zips = this.territoryZips();

        if (zips.length === 0) return;

        this.setState({ territory: { status: 'loading', zips: zips.length, missing: [] } });

        mapApi
            .zipBoundaries(zips)
            .then(({ boundaries, missing }) => {
                const bounds = this.territoryLayer.show(boundaries);

                if (bounds && !this.hasLinkedCenter()) this.map.fitBounds(bounds, TERRITORY_FIT_PADDING_PX);

                this.setState({ territory: { status: 'ready', zips: boundaries.length, missing } });

                return this.territoryLayer.drawOutline(boundaries);
            })
            .catch((error) => this.setState({
                territory: { status: 'error', zips: zips.length, missing: [] },
                toast: toast('Could not draw the territory outline: ' + error.message, 'error'),
            }, () => this.startToastTimer()));
    }

    /**
     * Address search. Google's autocomplete is already loaded for the POI
     * lookup, so it costs nothing extra to turn an address into a point.
     */
    initSearch() {
        if (!google.maps.places || !this.searchNode) return;

        const autocomplete = new google.maps.places.Autocomplete(this.searchNode, {
            fields: ['geometry'],
            componentRestrictions: { country: 'us' },
        });

        autocomplete.bindTo('bounds', this.map);
        autocomplete.addListener('place_changed', () => {
            this.setState({ isSearching: false });

            const place = autocomplete.getPlace();

            if (!place || !place.geometry) return;

            this.openSearchedParcel(place.geometry.location);
        });

        this.watchSearchPredictions();
    }

    /**
     * Open the parcel the searched address stands on.
     *
     * Google's viewport for a rooftop address is a 300m box — a whole block at
     * this map's size — so framing it left the user looking at much the same
     * parcels as before, with nothing saying which was theirs. What was
     * searched for is the parcel, so it is fetched and opened exactly as a
     * click on it would open it, and the map is framed on its boundary.
     */
    openSearchedParcel(location) {
        const lat = location.lat();
        const lng = location.lng();

        this.clearSearchMarker();
        this.setState(
            { step: 'summary', isLoadingSelection: true, selection: null, searchedParcelId: null },
            // The parcel the last search marked has to lose the mark now, not
            // when this one lands — a lookup that fails never gets that far.
            () => this.restyleParcels(),
        );

        mapApi
            .parcelAt(lat, lng)
            .then((result) => {
                this.fitToParcel(result.parcel);
                this.setState({ searchedParcelId: result.parcel.parcel_id }, () => this.openParcel(result));
            })
            .catch((error) => this.landWithoutParcel(lat, lng, error));
    }

    /**
     * Frame one parcel, clear of the chrome over the map.
     *
     * The zoom is fitBounds' alone. Nothing may be computed from getZoom()
     * here: straight after a fit it still answers with the zoom the map is
     * coming from, which is how `setZoom(max(getZoom(), …))` used to hold the
     * view exactly where it already was.
     */
    fitToParcel(parcel) {
        const bounds = parcelBounds(parcel);

        if (bounds) this.map.fitBounds(bounds, SEARCH_FIT_PADDING);
    }

    /**
     * A searched address the parcel feed holds nothing for — a town, a road,
     * or ground outside its coverage.
     *
     * The map still goes there, because a search that appears to do nothing is
     * worse than one that lands somewhere useless, but a marker and the toast
     * say plainly that there is no parcel rather than leaving the user hunting
     * for a highlight that was never drawn.
     */
    landWithoutParcel(lat, lng, error) {
        this.map.setCenter({ lat, lng });
        this.map.setZoom(SEARCH_FALLBACK_ZOOM);
        this.showSearchMarker(lat, lng);

        this.setState({
            step: null,
            isLoadingSelection: false,
            selection: null,
            toast: toast(lookupFailed(error, 'No parcel record at this address.'), 'error'),
        });
    }

    /** Where the search landed, for when no parcel could carry the mark. */
    showSearchMarker(lat, lng) {
        this.searchMarker = new google.maps.Marker({
            map: this.map,
            position: { lat, lng },
            clickable: false,
            zIndex: 6,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: ACTIVE_MARK,
                fillOpacity: 1,
                strokeColor: '#0B0D0E',
                strokeWeight: 2,
                scale: 7,
            },
        });
    }

    clearSearchMarker() {
        if (!this.searchMarker) return;

        this.searchMarker.setMap(null);
        this.searchMarker = null;
    }

    /** Drops the last search's mark, whichever form it took. */
    clearSearchMark() {
        this.clearSearchMarker();

        if (this.state.searchedParcelId === null) return;

        this.setState({ searchedParcelId: null }, () => this.restyleParcels());
    }

    /**
     * Turn the magnifier into a spinner while Google is fetching predictions.
     *
     * The Autocomplete widget fires nothing when a lookup starts or lands, so
     * the arrival of predictions is observed instead: the widget writes them
     * into a .pac-container it owns, and that appearing is the request having
     * answered. A ceiling clears the spinner if predictions never come — an
     * address with no matches produces no container at all, and a spinner left
     * turning on that would say "still working" forever.
     */
    watchSearchPredictions() {
        this.searchNode.addEventListener('input', () => {
            const term = this.searchNode.value.trim();

            clearTimeout(this.searchTimer);

            if (term === '') {
                this.setState({ isSearching: false });
                this.clearSearchMark();
                return;
            }

            this.setState({ isSearching: true });
            this.searchTimer = setTimeout(() => this.setState({ isSearching: false }), SEARCH_SPINNER_MAX_MS);
        });

        if (typeof MutationObserver === 'undefined') return;

        this.searchObserver = new MutationObserver(() => {
            const shown = Array.prototype.slice
                .call(document.querySelectorAll('.pac-container'))
                .some((node) => node.offsetHeight > 0);

            if (!shown || !this.state.isSearching) return;

            clearTimeout(this.searchTimer);
            this.setState({ isSearching: false });
        });

        this.searchObserver.observe(document.body, { childList: true, subtree: true });
    }

    handleShortcut(event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            if (this.searchNode) this.searchNode.focus();
            return;
        }

        if (event.key !== 'Escape') return;

        // The Kore window is innermost of all and answers Escape itself; closing
        // the panel too threw away the form it was opened from. The notes window too.
        if (this.state.leadModal || this.state.queueReview || this.state.notes) return;

        // The menu is the innermost thing open, so it goes first — Escape with
        // it open should not also close the panel behind it.
        if (this.state.isLayersOpen || this.state.isPinKeyOpen) {
            this.setState({ isLayersOpen: false, isPinKeyOpen: false });
            return;
        }

        if (this.state.step) this.closePanel();
    }

    setZoom(delta) {
        if (!this.map) return;

        this.map.setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.map.getZoom() + delta)));
    }

    /**
     * Show the loading state, but not for a fetch that answers straight away.
     *
     * A viewport already cached comes back in well under a tenth of a second,
     * and a bar that appears and vanishes in that time is just a flicker. Once
     * shown it is held, so the reverse — a spinner gone before it registered —
     * cannot happen either.
     */
    trackParcelLoading(isLoading) {
        clearTimeout(this.parcelLoadTimer);

        if (isLoading) {
            if (this.state.isLoadingParcels) return;

            this.parcelLoadTimer = setTimeout(() => {
                this.parcelLoadShownAt = Date.now();
                this.setState({ isLoadingParcels: true }, () => this.restyleParcels());
            }, SPINNER_DELAY_MS);

            return;
        }

        if (!this.state.isLoadingParcels) return;

        const shown = Date.now() - (this.parcelLoadShownAt || 0);

        this.parcelLoadTimer = setTimeout(
            () => this.setState({ isLoadingParcels: false }, () => this.restyleParcels()),
            Math.max(0, SPINNER_HOLD_MS - shown),
        );
    }

    // -------------------------------------------------------- parcel styling

    /**
     * What each parcel looks like right now.
     *
     * All of it lives here rather than in the layer because the answer depends
     * on things only this component knows: what is selected, what the pointer
     * is over — on the map or in the list — and what a drawn shape caught.
     */
    parcelStyle(parcelId) {
        return this.dimWhileLoading(this.parcelStyleAt(parcelId));
    }

    /**
     * Fade what is drawn while the next viewport is on its way.
     *
     * The layer keeps the old parcels up rather than clearing them, so without
     * this a stale set looks current. Dimming says "these are being replaced"
     * and costs no redraw of the geometry.
     */
    dimWhileLoading(style) {
        if (!this.state.isLoadingParcels) return style;

        return {
            ...style,
            strokeOpacity: style.strokeOpacity * 0.45,
            fillOpacity: style.fillOpacity * 0.45,
        };
    }

    /**
     * Parcel ids the current shape caught, as a set.
     *
     * Rebuilt only when the results change. It was a `shapeResults.some(...)`
     * inside the per-parcel style, which restyle then ran once per polygon —
     * a few thousand parcels against a few hundred results is close to a
     * million comparisons, and it ran again on every mouse-over.
     */
    shapeParcelIds() {
        const { shapeResults } = this.state;

        if (this.shapeIdsFor !== shapeResults) {
            this.shapeIdsFor = shapeResults;
            this.shapeIds = new Set(shapeResults.map((item) => item.parcel.parcel_id));
        }

        return this.shapeIds;
    }

    parcelStyleAt(parcelId) {
        const { selection, hoverParcelId, hoverRowId, excluded, searchedParcelId } = this.state;

        const outline = this.parcelLayer.get(parcelId);
        // Highlighted parcels are the ones in iCLIMB AND in the list filters
        // this map was opened with — the client's ask, not every DB row. With
        // the category filter on, only its matches: the client asked to see
        // those alone, not lit up among everything else. The territory view
        // counts a parcel one stands on too, as a match does: "all properties".
        const highlighted = this.state.matches !== null
            ? this.isMatchParcel(parcelId, outline)
            : this.isTerritoryView()
                ? this.isParcelOf('pins', this.pinIdSet(), parcelId, outline)
                : Boolean(outline && outline.property_id && this.pinIdSet().has(outline.property_id));
        const tint = this.isTerritoryView() ? TERRITORY_TINT : IN_ICLIMB_TINT;
        const inShape = this.shapeParcelIds().has(parcelId);
        const dropped = inShape && Boolean(excluded[parcelId]);
        const isActive = Boolean(selection && selection.parcel.parcel_id === parcelId) || searchedParcelId === parcelId;
        const isHover = hoverParcelId === parcelId || hoverRowId === parcelId;

        if (isActive) {
            return { fillColor: ACTIVE_MARK, fillOpacity: 0.5, strokeColor: '#3ECF8E', strokeOpacity: 1, strokeWeight: 3, zIndex: 4 };
        }

        if (dropped) {
            return { fillColor: '#000000', fillOpacity: 0.45, strokeColor: '#FFFFFF', strokeOpacity: 0.22, strokeWeight: 1, zIndex: 2 };
        }

        if (isHover) {
            return {
                fillColor: highlighted ? SUMMIT_BLUE : '#FFFFFF',
                fillOpacity: highlighted ? tint.hoverFill : 0.14,
                strokeColor: highlighted ? tint.stroke : '#FFFFFF',
                strokeOpacity: 1,
                strokeWeight: highlighted ? tint.hoverWeight : 2.4,
                zIndex: 3,
            };
        }

        if (inShape) {
            // Already ours, and still inside the shape: the Summit tint, so a
            // bulk add is visible on the map the moment it lands. Keeping the
            // selection blue here left the whole shape looking untouched.
            if (highlighted) {
                return { fillColor: SUMMIT_BLUE, fillOpacity: tint.shapeFill, strokeColor: '#9CC9FF', strokeOpacity: 1, strokeWeight: 1.6, zIndex: 2 };
            }

            return { fillColor: '#1570EF', fillOpacity: 0.3, strokeColor: '#9CC9FF', strokeOpacity: 1, strokeWeight: 1.6, zIndex: 2 };
        }

        // Resting state. Deliberately stronger than the design's hairline: over
        // satellite imagery a 1px line at 0.42 opacity disappears into driveways
        // and roof edges, and the boundaries were only really legible under the
        // pointer — which is no use for judging a block at a glance. The faint
        // fill is what makes a parcel read as an area rather than a stray line,
        // and hover still separates cleanly at weight 2.4 and full opacity.
        if (highlighted) {
            return { fillColor: SUMMIT_BLUE, fillOpacity: tint.fill, strokeColor: tint.stroke, strokeOpacity: 1, strokeWeight: tint.weight, zIndex: 1 };
        }

        return { fillColor: '#FFFFFF', fillOpacity: 0.05, strokeColor: '#FFFFFF', strokeOpacity: 0.82, strokeWeight: 1.4, zIndex: 1 };
    }

    restyleParcels() {
        if (this.parcelLayer) this.parcelLayer.restyle((parcelId) => this.parcelStyle(parcelId));
    }

    /**
     * Hover, restyling only the parcel gained and the one lost.
     *
     * Repainting the whole layer for a pointer move is the single heaviest
     * thing this view did per frame; two parcels change, so two are repainted.
     */
    setHover(key, parcelId) {
        const previous = this.state[key];

        if (previous === parcelId) return;

        this.setState({ [key]: parcelId }, () => {
            this.parcelLayer.styleFor = (id) => this.parcelStyle(id);
            this.parcelLayer.restyleOnly([previous, parcelId].filter(Boolean));
        });
    }

    toggleParcels() {
        if (!this.parcelLayer) return;

        const showParcels = !this.state.showParcels;

        this.parcelLayer.setEnabled(showParcels);
        this.setState({ showParcels }, () => this.syncToOutlines());
    }

    /**
     * Dots below the parcel zoom, boundaries above it, with the flagged pins
     * kept on top of their parcels. The blue dots come back whenever outlines
     * cannot draw (layer off, dense viewport, feed error), so the properties
     * never silently vanish. Zoomed in, the territory's wash lifts.
     */
    syncToOutlines() {
        if (!this.pinLayer) return;

        const { showParcels, zoom, parcelStatus } = this.state;
        const outlinesShown = showParcels
            && zoom >= PARCEL_MIN_ZOOM
            && (parcelStatus.status === 'ready' || parcelStatus.status === 'loading');

        this.pinLayer.setOverParcels(outlinesShown);
        if (this.territoryLayer) this.territoryLayer.setZoomedIn(zoom >= PARCEL_MIN_ZOOM);
    }

    // ------------------------------------------------------------- drawing

    /**
     * Arm a drawing tool, or pass null for the cursor — pan and select, which
     * is what the map does when nothing is armed.
     */
    pickTool(mode) {
        if (!this.shapeTool) return;

        const tool = this.shapeTool.toggle(mode);

        // A parcel or a pin under the cursor would otherwise take the press
        // that is meant to start the shape.
        this.parcelLayer.setInteractive(!tool);
        this.pinLayer.setInteractive(!tool);
        this.setState({ tool, isLayersOpen: false });
    }

    onShapeChange(measured) {
        const size = measured.type === 'box'
            ? { widthMiles: measured.widthMiles, heightMiles: measured.heightMiles }
            : { radiusMiles: measured.radiusMiles };

        this.setState((prev) => ({ shapeType: measured.type, size: { ...prev.size, ...size } }));
    }

    searchShape() {
        const wkt = this.shapeTool.toWkt();

        if (!wkt) return;

        this.parcelLayer.setInteractive(true);
        this.pinLayer.setInteractive(true);

        this.setState({ step: 'selection', isLoadingShape: true, tool: null });

        mapApi
            .parcelsWithin(wkt, 250, this.state.includeResidential)
            .then((response) =>
                this.setState({ isLoadingShape: false, shapeResults: response.data }, () => this.restyleParcels())
            )
            .catch((error) => {
                console.warn('Shape lookup failed:', error.message);
                this.setState({
                    isLoadingShape: false,
                    toast: toast('Could not look up parcels in that shape. Try again.', 'error'),
                }, () => this.startToastTimer());
            });
    }

    clearShape() {
        if (this.shapeTool) this.shapeTool.clear();
        this.setState(
            { step: null, shapeType: null, shapeResults: [], excluded: {}, tool: null },
            () => this.restyleParcels()
        );
    }

    // ------------------------------------------------------------ selecting

    scheduleClick(event) {
        clearTimeout(this.clickTimer);

        // A click away from a drawn shape dismisses it, and does only that.
        // Clear was the sole way out of a box or a circle, which meant hunting
        // for a button in the panel to undo something drawn on the map.
        if (this.shapeTool && this.shapeTool.hasShape() && !this.shapeTool.containsPoint(event.latLng)) {
            this.clearShape();
            return;
        }

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const placeId = event.placeId;

        this.clickTimer = setTimeout(() => this.openParcelAt(lat, lng, placeId), DOUBLE_CLICK_GRACE_MS);
    }

    openParcelAt(lat, lng, placeId) {
        this.clearSearchMark();
        this.setState({ step: 'summary', isLoadingSelection: true, selection: null });

        Promise.all([mapApi.parcelAt(lat, lng), this.placeName(placeId)])
            .then(([result, placeName]) => {
                if (placeName) result.suggested = { ...result.suggested, NAME: placeName };
                this.openParcel(result);
            })
            .catch((error) =>
                this.setState({
                    isLoadingSelection: false,
                    step: null,
                    toast: toast(lookupFailed(error, 'No parcel here.'), 'error'),
                })
            );
    }

    /** Show a parcel we already hold the record for. */
    openParcel(result) {
        const existing = result.existing_property;

        // The payload belongs to the parcel it was written for; another one
        // starts fresh rather than editing the first parcel's form.
        this.handoffToken = null;

        this.setState(
            {
                step: 'summary',
                isLoadingSelection: Boolean(existing),
                selection: {
                    parcel: result.parcel,
                    suggested: result.suggested,
                    existing,
                    property: null,
                    owner_candidates: result.owner_candidates || [],
                    owner_match_id: result.owner_match_id || '',
                    territory: result.territory || null,
                },
                owner: result.owner_match_id
                    ? { ...EMPTY_OWNER, kind: 'investor', id: result.owner_match_id }
                    : { ...EMPTY_OWNER, kind: 'record' },
                foundInvestors: [],
                leadChecks: {},
                leadDecisions: {},
                leadWaiting: null,
                // The assessor naming nobody is the case this flag exists for,
                // so it starts on rather than waiting to be found.
                needOwner: !result.parcel.owner_name,
                form: null,
                diffRows: [],
                diffRejected: {},
                diffError: null,
            },
            () => {
                this.restyleParcels();

                // Asked while the card is read, so Add rarely has to wait for Kore.
                if (!existing) this.checkLeads();
            }
        );

        if (!existing) return;

        this.loadProperty(existing.id);
    }

    /**
     * iCLIMB's own record behind the open card: the investor, the territory and
     * the filing the parcel itself knows nothing about. Every route that puts a
     * property on the card fetches it here, the add included — without it the
     * card says "In iCLIMB" over an empty investor section.
     */
    loadProperty(propertyId, followLeads = false) {
        mapApi
            .property(propertyId)
            .then((property) =>
                this.setState((prev) => ({
                    isLoadingSelection: false,
                    selection: prev.selection && prev.selection.existing && prev.selection.existing.id === propertyId
                        ? { ...prev.selection, property }
                        : prev.selection,
                }), () => followLeads && this.followNewLeads(propertyId))
            )
            .catch((error) => this.setState({ isLoadingSelection: false, toast: toast(error.message, 'error') }));
    }

    /** Re-read the property on the card until every owner on it has a Kore lead. */
    followNewLeads(propertyId, triesLeft = LEAD_POLL_TRIES) {
        clearTimeout(this.leadPollTimer);

        const { selection } = this.state;
        const shown = selection && selection.existing && selection.existing.id === propertyId && selection.property;

        if (!shown || triesLeft === 0 || (shown.owners || []).every((owner) => owner.crm_lead_id)) return;

        this.leadPollTimer = setTimeout(() => {
            mapApi
                .property(propertyId)
                .then((property) =>
                    this.setState((prev) => ({
                        selection: prev.selection && prev.selection.existing && prev.selection.existing.id === propertyId
                            ? { ...prev.selection, property }
                            : prev.selection,
                    }), () => this.followNewLeads(propertyId, triesLeft - 1))
                )
                .catch((error) => this.setState({ toast: toast(error.message, 'error') }, () => this.startToastTimer()));
        }, LEAD_POLL_MS);
    }

    /**
     * Open the card for a property iCLIMB already holds.
     *
     * The pin click knows where it is, so both requests go at once. A link
     * that names only the property has to ask for it first and take the
     * coordinates off the answer.
     *
     * @returns {Promise<object|null>} the parcel found under it, if any.
     */
    openProperty(propertyId, pin) {
        this.handoffToken = null;
        this.clearSearchMark();
        this.setState({ step: 'summary', isLoadingSelection: true, selection: null });

        const load = pin
            ? Promise.all([mapApi.property(propertyId), mapApi.parcelAt(pin.lat, pin.lng).catch(() => null)])
            : mapApi.property(propertyId).then((property) => {
                  const lat = parseFloat(property.latitude);
                  const lng = parseFloat(property.longitude);

                  if (isNaN(lat) || isNaN(lng)) return [property, null];

                  return mapApi
                      .parcelAt(lat, lng)
                      .catch(() => null)
                      .then((result) => [property, result]);
              });

        return load
            .then(([property, result]) => {
                this.setState(
                    {
                        isLoadingSelection: false,
                        selection: {
                            parcel: result ? result.parcel : parcelFromProperty(property),
                            suggested: result ? result.suggested : {},
                            existing: {
                                id: property.id,
                                name: property.name,
                                status: property.status,
                                status_bucket: property.status_bucket,
                            },
                            // The parcel lookup answers with these; dropping them
                            // left a property opened from a pin with no iCLIMB
                            // list at all, where the same one opened by clicking
                            // its parcel had one.
                            owner_candidates: (result && result.owner_candidates) || [],
                            owner_match_id: (result && result.owner_match_id) || '',
                            territory: (result && result.territory) || null,
                            property,
                        },
                        owner: result && result.owner_match_id
                            ? { ...EMPTY_OWNER, kind: 'investor', id: result.owner_match_id }
                            : EMPTY_OWNER,
                        foundInvestors: [],
                        leadChecks: {},
                        leadDecisions: {},
                        leadWaiting: null,
                        needOwner: false,
                        form: null,
                        diffRows: [],
                        diffRejected: {},
                        diffError: null,
                    },
                    () => this.restyleParcels()
                );

                // An object either way, so a caller can tell "the property
                // loaded but sits on no parcel" from "the property did not
                // load" — the two need different things said about them.
                return { parcel: result ? result.parcel : null };
            })
            .catch((error) => {
                this.setState({ isLoadingSelection: false, step: null, toast: toast(error.message, 'error') });

                return null;
            });
    }

    /**
     * ?property= lands the map on one property, framed and marked the way a
     * search does — the properties list links here per row.
     */
    openLinkedProperty() {
        const query = (this.props.location && this.props.location.query) || {};
        const propertyId = query.property;

        if (!propertyId) return;

        this.openProperty(propertyId, null).then((result) => {
            // A failure already said why; only silence needs filling in.
            if (result === null) return;

            if (result.parcel === null) {
                this.setState({ toast: toast('No parcel found for this property.', 'error') });

                return;
            }

            this.revealResidentialFor(result.parcel);
            this.fitToParcel(result.parcel);
            this.setState({ searchedParcelId: result.parcel.parcel_id }, () => this.restyleParcels());
        });
    }

    /**
     * A house is not drawn at all with the residential layer off, so a link to
     * one would open its card over an empty map. Turning the layer on is the
     * only way the parcel it names can be seen.
     */
    revealResidentialFor(parcel) {
        const isResidential = String(parcel.land_use_code || '').slice(0, 2) === RESIDENTIAL_LAND_USE_GROUP;

        if (!isResidential || this.state.includeResidential) return;

        this.setState({ includeResidential: true }, () => {
            if (this.parcelLayer) this.parcelLayer.setIncludeResidential(true);
            this.writeResidentialToUrl(true);
        });
    }

    /**
     * The business name Google shows for a clicked POI. The client asked for
     * this to become the property name.
     */
    placeName(placeId) {
        if (!placeId || !google.maps.places) return Promise.resolve(null);

        return new Promise((resolve) => {
            const service = new google.maps.places.PlacesService(this.map);

            service.getDetails({ placeId, fields: ['name'] }, (place, status) =>
                resolve(status === google.maps.places.PlacesServiceStatus.OK && place ? place.name : null)
            );
        });
    }

    // -------------------------------------------------------------- adding

    /** Double click on the map: add straight away, no form. */
    quickAddAt(lat, lng, placeId) {
        this.setState({ isSaving: true });

        Promise.all([mapApi.parcelAt(lat, lng), this.placeName(placeId)])
            .then(([result, placeName]) =>
                mapApi.createProperty(result.parcel, placeName ? { NAME: placeName } : {}).then((property) => this.afterCreate(property, result))
            )
            .catch((error) =>
                this.setState({
                    isSaving: false,
                    toast: toast(lookupFailed(error, 'No parcel here.'), 'error'),
                })
            );
    }

    /**
     * One field of the add form.
     *
     * What a change makes unreachable has to go with it, or a tenant
     * subcategory chosen for a retail single-tenant is saved against an
     * apartment. Which fields those are is the form's own question.
     */
    onFormField(key, value) {
        this.setState((prev) => {
            const form = { ...prev.form, [key]: value };

            fieldsClearedBy(key, value).forEach((dependent) => { delete form[dependent]; });

            return { form };
        });
    }

    saveForm() {
        const { selection, form } = this.state;
        const key = this.leadKey();

        this.setState({ isSaving: true });

        mapApi
            .createProperty(selection.parcel, numeric(form), this.ownerPayload())
            .then((property) => this.afterCreate(property, selection, 'summary'))
            .catch((error) => this.setState((prev) => ({
                isSaving: false,
                toast: toast(error.message, 'error'),
                leadDecisions: withoutLeadDecision(prev.leadDecisions, key),
            })));
    }

    /**
     * A new property is one more pin and one more tinted parcel, so both are
     * patched in place rather than re-downloading all 85k of them.
     */
    afterCreate(property, result, step) {
        const existing = {
            id: property.id,
            name: property.name,
            status: property.status,
            status_bucket: property.status_bucket,
        };

        this.markInDb({ [result.parcel.parcel_id]: existing });

        // Added here, it has no status yet, so no flag.
        const pins = property.created
            ? (this.state.pins || []).concat({
                id: property.id,
                lat: result.parcel.latitude,
                lng: result.parcel.longitude,
                status: property.status,
            })
            : this.state.pins;

        this.setState(
            (prev) => ({
                pins,
                isSaving: false,
                step: step || prev.step,
                selection: prev.selection && prev.selection.parcel.parcel_id === result.parcel.parcel_id
                    ? { ...prev.selection, existing }
                    : prev.selection,
                owner: EMPTY_OWNER,
                // An answer belongs to the save it was given for, not the next one.
                leadChecks: {},
                leadDecisions: {},
                ownerSavedAt: Date.now(),
                toast: toast(
                    property.created
                        ? property.name + ' added to iCLIMB'
                        : property.name + ' is already in iCLIMB',
                    'ok',
                    property.created ? property.id : null
                ),
            }),
            () => {
                if (property.created) this.pinLayer.setPins(pins);
                this.restyleParcels();
                this.startToastTimer();

                if (this.state.selection && this.state.selection.existing
                    && this.state.selection.existing.id === property.id) {
                    this.loadProperty(property.id, true);
                }
            }
        );
    }

    bulkAdd(parcels, decisions) {
        this.setState({ isSaving: true });

        mapApi
            .createProperties(parcels, decisions)
            .then((response) => {
                const byParcel = {};
                const pins = (this.state.pins || []).slice();

                response.created.forEach((created) => {
                    byParcel[created.parcel_id] = {
                        id: created.id,
                        name: created.name,
                        status: created.status,
                        status_bucket: created.status_bucket,
                    };
                    pins.push({ id: created.id, lat: created.lat, lng: created.lng, status: created.status });
                });

                this.markInDb(byParcel);

                this.setState({
                    pins,
                    isSaving: false,
                    leadModal: null,
                    bulkAddRun: null,
                    bulkAnswers: {},
                    toast: toast(response.created.length + ' properties added to iCLIMB', 'ok'),
                }, () => {
                    this.pinLayer.setPins(pins);
                    this.restyleParcels();
                    this.startToastTimer();
                });
            })
            // The window, when open, covers the toast row, so the reason goes there.
            .catch((error) => this.setState((prev) => (prev.leadModal
                ? { isSaving: false, leadModal: { ...prev.leadModal, notice: error.message } }
                : { isSaving: false, bulkAddRun: null, toast: toast(error.message, 'error') })));
    }

    bulkUpdate(kept) {
        this.setState({ isSaving: true });

        mapApi
            .applyParcelBulk(kept.map((result) => result.existing_property.id))
            .then((response) =>
                this.setState(
                    { isSaving: false, toast: toast(response.updated + ' properties updated from LightBox', 'ok') },
                    () => this.startToastTimer()
                )
            )
            .catch((error) => this.setState({ isSaving: false, toast: toast(error.message, 'error') }));
    }

    /** Record, in every list holding it, that a parcel is now ours. */
    markInDb(byParcelId) {
        this.parcelLayer.getOutlines().forEach((outline) => {
            const property = byParcelId[outline.parcel_id];

            if (property) {
                outline.property_id = property.id;
                outline.status_bucket = property.status_bucket;
            }
        });

        this.setState((prev) => ({
            shapeResults: prev.shapeResults.map((result) =>
                byParcelId[result.parcel.parcel_id]
                    ? { ...result, existing_property: byParcelId[result.parcel.parcel_id] }
                    : result
            ),
        }));
    }

    undoAdd(propertyId) {
        mapApi
            .deleteProperty(propertyId)
            .then(() => {
                const pins = (this.state.pins || []).filter((pin) => pin.id !== propertyId);

                this.parcelLayer.getOutlines().forEach((outline) => {
                    if (outline.property_id === propertyId) {
                        outline.property_id = null;
                        outline.status_bucket = null;
                    }
                });

                this.setState(
                    (prev) => ({
                        pins,
                        toast: null,
                        selection: prev.selection && prev.selection.existing && prev.selection.existing.id === propertyId
                            ? { ...prev.selection, existing: null, property: null }
                            : prev.selection,
                    }),
                    () => {
                        this.pinLayer.setPins(pins);
                        this.restyleParcels();
                    }
                );
            })
            .catch((error) => this.setState({ toast: toast(error.message, 'error') }));
    }

    /**
     * Hand the parcel to the property form and let its Save add it.
     *
     * The form's own Save writes the property row and nothing else — it knows
     * neither the assessor's owner nor the choice made on the card — so the
     * whole payload travels with it and the form posts it back through the
     * map's create, which is what attaches the investor and NEED_OWNER.
     */
    openFullForm() {
        const token = writeParcelHandoff(this.handoffPayload(), this.handoffToken);

        if (token === null) {
            this.setState({ toast: toast('This browser would not carry the parcel to the property form.', 'error') });

            return;
        }

        this.handoffToken = token;
        window.open('/property?fromParcel=' + token, '_blank');
    }

    /** The owner choice, marked as the user's rather than the card's default. */
    pickOwner(owner) {
        // A save left waiting on Kore was about the owner before this one.
        this.setState({ owner: { ...owner, touched: true }, leadWaiting: null }, () => {
            this.syncHandoff();
            this.checkLeads();
        });
    }

    /** Who was picked on the card, in the one shape both endpoints take. */
    ownerChoice() {
        const { owner, selection } = this.state;
        const decision = this.state.leadDecisions[this.leadKey()] || {};

        return {
            needOwner: false,
            parcelId: selection.parcel.parcel_id,
            ownerName: selection.parcel.owner_name,
            investorId: owner.kind === 'investor' ? owner.id : null,
            ownerOfRecord: owner.kind === 'record',
            trueOwnerKey: owner.kind === 'true' ? owner.key : null,
            contact: owner.kind === 'true' ? owner.contact : {},
            fields: owner.kind === 'true' ? owner.fields : [],
            koreLeadId: decision.koreLeadId || null,
            createNew: Boolean(decision.createNew),
        };
    }

    // --------------------------------------------------------- Kore duplicates

    /**
     * Where Kore's answer is kept for the owner this save would file, or null
     * when it files nobody new: no owner, or one iCLIMB already holds that the
     * card has not said to file new anyway.
     */
    leadKey() {
        const { owner, selection, needOwner } = this.state;

        if (!selection || needOwner) return null;
        if (owner.kind === 'true' && (!owner.existing || owner.asNew)) return 'true:' + selection.parcel.parcel_id + ':' + owner.key;
        if (owner.kind === 'record') return 'record:' + selection.parcel.parcel_id;

        return null;
    }

    /**
     * Ask Kore about the owner the card would file, quietly, as soon as there
     * is one, so the save rarely waits on it. One answer per owner.
     */
    checkLeads(force) {
        const key = this.leadKey();

        if (!key) return;

        const known = this.state.leadChecks[key];

        if (!force && known && known.status !== 'error') return;

        const { owner, selection } = this.state;
        const request = {
            parcel: selection.parcel,
            true_owner_key: owner.kind === 'true' ? owner.key : undefined,
            owner_of_record: owner.kind === 'record' ? true : undefined,
            // Filing someone new beside the record on file: that record's own
            // email and phones are part of the question, or its lead is missed.
            check_held: owner.kind === 'true' && owner.existing ? true : undefined,
        };

        this.setLeadCheck(key, { status: 'loading' });

        // Asked by the record's own details, Kore answers with the record's own lead: already on file, not a match.
        const heldLeadId = request.check_held ? owner.heldLeadId : null;

        mapApi
            .leadMatches([request])
            .then((results) => {
                const matches = results[0].matches.filter((match) => !heldLeadId || String(match.lead_id) !== String(heldLeadId));

                this.setLeadCheck(key, {
                    status: 'done',
                    needsCheck: results[0].needs_check,
                    matches,
                    total: results[0].total - (results[0].matches.length - matches.length),
                });
            })
            .catch((error) => this.setLeadCheck(key, { status: 'error', error: leadCheckProblem(error) }));
    }

    setLeadCheck(key, check) {
        this.setState(
            (prev) => ({ leadChecks: { ...prev.leadChecks, [key]: check } }),
            () => this.resumeLeadWait(key)
        );
    }

    /**
     * Run a save ('add', 'saveOwner' or 'fullForm') once Kore has answered: straight
     * on when nothing matched, through the duplicate window when something did.
     */
    withLeadCheck(action) {
        const key = this.leadKey();

        if (!key || this.state.leadDecisions[key]) {
            this.runSave(action);

            return;
        }

        const check = this.state.leadChecks[key];

        if (!check || check.status !== 'done') {
            const ask = () => {
                if (!check || check.status === 'error') this.checkLeads(true);
            };

            // A tab can only be opened inside the click, so the full form
            // waits in the window, where the next click is.
            if (action === 'fullForm') {
                this.setState({ leadModal: { mode: 'single', action, key, status: 'loading' } }, ask);
            } else {
                this.setState({ leadWaiting: action }, ask);
            }

            return;
        }

        if (!check.needsCheck || check.matches.length === 0) {
            this.decideLead(key, { createNew: true }, () => this.runSave(action));

            return;
        }

        this.setState({ leadWaiting: null, leadModal: { mode: 'single', action, key, status: 'ready' } });
    }

    /** The Kore answer a save was waiting for has come back. */
    resumeLeadWait(key) {
        const { leadWaiting, leadChecks, leadModal } = this.state;
        const inWindow = leadModal && leadModal.mode === 'single' && leadModal.status === 'loading' && leadModal.key === key;
        const action = inWindow ? leadModal.action : leadWaiting;
        const check = leadChecks[key];

        if (!action || this.leadKey() !== key || check.status === 'loading') return;

        if (check.status === 'error') {
            this.setState({ leadWaiting: null, leadModal: { mode: 'single', action, key, status: 'error', error: check.error } });

            return;
        }

        if (action === 'fullForm' && (!check.needsCheck || check.matches.length === 0)) {
            this.setState({ leadWaiting: null, leadModal: { mode: 'single', action, key, status: 'clear' } });

            return;
        }

        this.setState({ leadWaiting: null, leadModal: null }, () => this.withLeadCheck(action));
    }

    decideLead(key, decision, then) {
        this.setState(
            (prev) => ({ leadDecisions: { ...prev.leadDecisions, [key]: decision } }),
            () => {
                this.syncHandoff();

                if (then) then();
            }
        );
    }

    runSave(action) {
        if (action === 'add') this.saveForm();
        if (action === 'saveOwner') this.saveOwner();
        if (action === 'fullForm') this.openFullForm();
    }

    /** Who the single window says is being added: the picked contact, or the roll's entity. */
    leadAdding() {
        const { owner, selection } = this.state;

        if (owner.kind === 'true') {
            return {
                name: [owner.name, owner.company].filter(Boolean).join(' · '),
                lines: [
                    [owner.contact.cell || owner.contact.phone, owner.contact.email].filter(Boolean).join(' · '),
                    'The contact you picked on the card',
                ],
            };
        }

        return { name: selection.parcel.owner_name, lines: [selection.parcel.owner_address] };
    }

    /**
     * A drawn shape's add, once Kore has been asked about every owner it would
     * file — in chunks, so the button can count them off.
     */
    startBulkAdd(kept) {
        const run = { id: Date.now(), parcels: kept.map((result) => result.parcel), results: {} };

        this.setState({ bulkAddRun: run, bulkAnswers: {}, bulkCheck: { done: 0, total: run.parcels.length } }, () => this.checkBulk(run.id, 0));
    }

    checkBulk(runId, from) {
        const run = this.state.bulkAddRun;

        if (!run || run.id !== runId) return;

        if (from >= run.parcels.length) {
            this.afterBulkCheck();

            return;
        }

        const chunk = run.parcels.slice(from, from + BULK_CHECK_CHUNK);

        mapApi
            .leadMatches(chunk.map((parcel) => ({ parcel })))
            .then((results) => {
                const found = {};

                results.forEach((result) => { found[result.parcel_id] = result; });

                this.setState(
                    (prev) => (prev.bulkAddRun && prev.bulkAddRun.id === runId ? {
                        bulkAddRun: { ...prev.bulkAddRun, results: { ...prev.bulkAddRun.results, ...found } },
                        bulkCheck: { done: from + chunk.length, total: run.parcels.length },
                    } : null),
                    () => this.checkBulk(runId, from + chunk.length)
                );
            })
            .catch((error) => this.setState((prev) => (prev.bulkAddRun && prev.bulkAddRun.id === runId ? {
                bulkCheck: null,
                leadModal: { mode: 'bulk', status: 'error', error: leadCheckProblem(error), resumeFrom: from },
            } : null)));
    }

    /** Nothing matched: add as before. Something did: ask, property by property. */
    afterBulkCheck() {
        const run = this.state.bulkAddRun;
        const keys = run.parcels
            .filter((parcel) => {
                const result = run.results[parcel.parcel_id];

                return result && result.needs_check && result.matches.length > 0;
            })
            .map((parcel) => parcel.parcel_id);

        if (keys.length === 0) {
            this.setState({ bulkCheck: null }, () => this.bulkAdd(run.parcels, this.bulkDecisions({})));

            return;
        }

        this.setState({ bulkCheck: null, leadModal: { mode: 'bulk', status: 'ready', keys, activeKey: keys[0], notice: null } });
    }

    /** Each parcel's answer: the user's where Kore matched, "file it" everywhere else. */
    bulkDecisions(answers) {
        return this.state.bulkAddRun.parcels.map((parcel) => {
            const answer = answers[parcel.parcel_id];

            return answer && answer.koreLeadId
                ? { parcel_id: parcel.parcel_id, kore_lead_id: answer.koreLeadId }
                : { parcel_id: parcel.parcel_id, create_new: true };
        });
    }

    /** One property answered; the window moves on to the next one still open. */
    answerBulk(key, answer) {
        this.setState((prev) => {
            const answers = { ...prev.bulkAnswers, [key]: answer };
            const open = prev.leadModal.keys.filter((each) => !answers[each]);

            return {
                bulkAnswers: answers,
                leadModal: { ...prev.leadModal, activeKey: open[0] || key, notice: null },
            };
        });
    }

    confirmBulk() {
        const { leadModal, bulkAnswers, bulkAddRun } = this.state;
        const open = leadModal.keys.filter((key) => !bulkAnswers[key]);

        if (open.length > 0) {
            this.setState({
                leadModal: {
                    ...leadModal,
                    activeKey: open[0],
                    notice: 'Choose for the ' + open.length + ' propert' + (open.length === 1 ? 'y' : 'ies') + ' still open.',
                },
            });

            return;
        }

        this.bulkAdd(bulkAddRun.parcels, this.bulkDecisions(bulkAnswers));
    }

    closeLeadModal() {
        const { leadModal } = this.state;

        this.setState(leadModal && leadModal.mode === 'bulk'
            ? { leadModal: null, bulkAddRun: null, bulkAnswers: {}, bulkCheck: null }
            : { leadModal: null, leadWaiting: null });
    }

    retryLeadCheck() {
        const { leadModal, bulkAddRun } = this.state;

        if (leadModal.mode === 'bulk') {
            this.setState(
                { leadModal: null, bulkCheck: { done: leadModal.resumeFrom, total: bulkAddRun.parcels.length } },
                () => this.checkBulk(bulkAddRun.id, leadModal.resumeFrom)
            );

            return;
        }

        this.setState({ leadModal: { ...leadModal, status: 'loading' } }, () => this.checkLeads(true));
    }

    /** The property's notes, from the header of a card iCLIMB holds. */
    renderPanelActions() {
        const { step, selection } = this.state;

        if (step !== 'summary' || !selection || !selection.existing) return null;

        const count = selection.property ? selection.property.notes_count : 0;

        return (
            <button type="button" className="SidePanel-notes" onClick={() => this.openPropertyNotes()}>
                <NotesIcon />
                Notes
                {count > 0 && <span className="SidePanel-notesCount">{count}</span>}
            </button>
        );
    }

    renderNotes() {
        const { notes } = this.state;

        if (!notes) return null;

        const isInvestor = notes.owner.type === 'investor';

        return (
            <NotesWindow
                owner={notes.owner}
                title={notes.title}
                subtitle={notes.subtitle}
                links={notes.links}
                theme="dark"
                deletePrompt={isInvestor ? 'Delete? It’s removed from Kore too.' : 'Delete this note?'}
                onClose={() => this.setState({ notes: null })}
                onChange={isInvestor ? undefined : (list) => this.setPropertyNotesCount(notes.owner.id, list.length)}
            />
        );
    }

    renderLeadModal() {
        const { leadModal, leadChecks, leadDecisions, bulkAddRun, bulkAnswers, isSaving } = this.state;

        if (!leadModal) return null;

        if (leadModal.mode === 'bulk') {
            const results = bulkAddRun ? bulkAddRun.results : {};
            const byId = {};

            (bulkAddRun ? bulkAddRun.parcels : []).forEach((parcel) => { byId[parcel.parcel_id] = parcel; });

            const items = (leadModal.keys || []).map((key) => ({
                key,
                title: byId[key].address || 'Parcel ' + key,
                subtitle: byId[key].owner_name,
                adding: { name: byId[key].owner_name, lines: [byId[key].owner_address, 'For ' + (byId[key].address || 'parcel ' + key)] },
                matches: results[key].matches,
                total: results[key].total,
            }));

            return (
                <LeadMatchesModal
                    mode="bulk"
                    status={leadModal.status}
                    error={leadModal.error}
                    items={items}
                    activeKey={leadModal.activeKey}
                    decisions={bulkAnswers}
                    total={bulkAddRun ? bulkAddRun.parcels.length : 0}
                    notice={leadModal.notice}
                    isSaving={isSaving}
                    onActivate={(key) => this.setState({ leadModal: { ...leadModal, activeKey: key, notice: null } })}
                    onUse={(key, match) => this.answerBulk(key, { koreLeadId: match.lead_id })}
                    onCreateNew={(key) => this.answerBulk(key, { createNew: true })}
                    onConfirm={() => this.confirmBulk()}
                    onRetry={() => this.retryLeadCheck()}
                    onCancel={() => this.closeLeadModal()}
                />
            );
        }

        const check = leadChecks[leadModal.key] || {};
        const decide = (decision) => this.setState({ leadModal: null }, () =>
            this.decideLead(leadModal.key, decision, () => this.runSave(leadModal.action)));

        return (
            <LeadMatchesModal
                mode="single"
                status={leadModal.status}
                error={leadModal.error}
                items={[{ key: leadModal.key, adding: this.leadAdding(), matches: check.matches || [], total: check.total || 0 }]}
                activeKey={leadModal.key}
                decisions={{ [leadModal.key]: leadDecisions[leadModal.key] }}
                confirmLabel="Open full form ↗"
                onUse={(key, match) => decide({ koreLeadId: match.lead_id })}
                onCreateNew={() => decide({ createNew: true })}
                onConfirm={() => decide({ createNew: true })}
                onRetry={() => this.retryLeadCheck()}
                onCancel={() => this.closeLeadModal()}
            />
        );
    }

    /**
     * What an add does about the owner.
     *
     * Flagging for research is an answer only the add has: a property already
     * in iCLIMB carries the flag as a fact, and attaching an owner is the one
     * thing that clears it.
     */
    ownerPayload() {
        return this.state.needOwner ? { needOwner: true } : this.ownerChoice();
    }

    /**
     * Give a property iCLIMB already holds the owner picked on the card.
     *
     * The only route to it from the map: the LightBox diff offers PROPERTIES
     * columns, and ownership lives in its own tables.
     */
    saveOwner() {
        const { selection } = this.state;
        const key = this.leadKey();
        const alongside = Boolean(this.state.owner.alongside);

        this.setState({ isSavingOwner: true });

        mapApi
            .setPropertyOwner(selection.existing.id, { ...this.ownerChoice(), alongside })
            .then((result) =>
                mapApi.property(selection.existing.id).then((property) =>
                    this.setState((prev) => ({
                        isSavingOwner: false,
                        owner: EMPTY_OWNER,
                        leadChecks: {},
                        leadDecisions: {},
                        ownerSavedAt: Date.now(),
                        selection: prev.selection && prev.selection.existing
                            && prev.selection.existing.id === selection.existing.id
                            ? { ...prev.selection, property }
                            : prev.selection,
                        toast: toast(result.investor.name + (alongside ? ' added as co-owner' : ' is now the owner'), 'ok'),
                    }), () => {
                        this.startToastTimer();
                        this.followNewLeads(selection.existing.id);
                    })
                )
            )
            .catch((error) => this.setState((prev) => ({
                isSavingOwner: false,
                toast: toast(error.message, 'error'),
                leadDecisions: withoutLeadDecision(prev.leadDecisions, key),
            })));
    }

    /**
     * The owner-research flag, from the card.
     *
     * On a parcel nobody holds there is no record to write to, so the answer
     * stays in state until the add carries it. On one iCLIMB holds it is the
     * record's own flag and goes straight to the server — the client asked to
     * be able to say "this owner is right, no research needed" without leaving
     * the map.
     *
     * @param {boolean} needOwner
     */
    changeNeedOwner(needOwner) {
        const { selection } = this.state;
        const existing = selection && selection.existing;

        if (!existing) {
            this.setState({ needOwner, leadWaiting: null }, () => this.syncHandoff());

            return;
        }

        mapApi
            .setNeedOwner(existing.id, needOwner)
            .then(() => mapApi.property(existing.id))
            .then((property) =>
                this.setState((prev) => ({
                    selection: prev.selection && prev.selection.existing
                        && prev.selection.existing.id === existing.id
                        ? { ...prev.selection, property }
                        : prev.selection,
                })))
            .catch((error) => this.setState({ toast: toast(error.message, 'error') }, () => this.startToastTimer()));
    }

    /** @returns {object} everything the property form's save needs from here. */
    handoffPayload() {
        const { selection, form } = this.state;

        return {
            parcel: selection.parcel,
            overrides: numeric({ ...selection.suggested, ...(form || {}) }),
            ownerChoice: this.ownerPayload(),
            // The property form has no way to look this up: the choice lives
            // here until the save that makes the property makes it true.
            owner: this.ownerSummary(),
        };
    }

    /**
     * Changing the owner after the form is already open has to reach it, or
     * that tab saves the owner chosen a minute ago and says nothing about it.
     * Same token, so the open form follows rather than needing a new tab.
     */
    syncHandoff() {
        if (this.handoffToken === null || !this.state.selection) return;

        writeParcelHandoff(this.handoffPayload(), this.handoffToken);
    }

    /** What the pending owner choice says, for the form that cannot see it. */
    ownerSummary() {
        const { owner, selection, needOwner } = this.state;
        const ownerName = selection.parcel.owner_name;

        if (needOwner) {
            return { label: 'Flagged for owner research — no investor attached.', sub: '' };
        }

        if (owner.kind === 'record') {
            return {
                label: ownerName ? 'A new investor for “' + ownerName + '”' : 'A new investor',
                sub: 'The assessor’s owner name, filed as-is.',
            };
        }

        if (owner.kind === 'true') {
            return { label: 'The true owner chosen on the parcel panel', sub: 'From LightBox' };
        }

        const known = (selection.owner_candidates || []).concat(this.state.foundInvestors || []);
        const chosen = known.filter((candidate) => candidate.id === owner.id)[0];

        return {
            label: chosen ? chosen.name : 'The investor chosen on the parcel panel',
            sub: chosen ? [chosen.company, chosen.reason].filter(Boolean).join(' · ') : '',
        };
    }

    // -------------------------------------------------------------- updating

    openDiff() {
        const { selection } = this.state;

        this.setState({ step: 'update', isLoadingDiff: true, diffRows: [], diffRejected: {}, diffError: null });

        mapApi
            .parcelDiff(selection.existing.id)
            .then((result) => this.setState({ isLoadingDiff: false, diffRows: result.rows }))
            .catch((error) =>
                // Stay on the update step. Bouncing back to the summary read as
                // the button doing nothing at all.
                this.setState({
                    isLoadingDiff: false,
                    diffError: error.code === 'NO_PARCEL'
                        ? 'LightBox has no parcel at this property’s location, so there is nothing to compare.'
                        : error.message,
                })
            );
    }

    applyDiff() {
        const { selection, diffRows, diffRejected } = this.state;
        const fields = diffRows.filter((row) => !diffRejected[row.key]).map((row) => row.key);

        this.setState({ isSaving: true });

        mapApi
            .applyParcel(selection.existing.id, fields)
            .then(() => {
                this.setState({ isSaving: false, step: 'summary', toast: toast(fields.length + ' fields updated from LightBox', 'ok') }, () => this.startToastTimer());
                this.openParcel({ parcel: selection.parcel, suggested: selection.suggested, existing_property: selection.existing });
            })
            .catch((error) => this.setState({ isSaving: false, toast: toast(error.message, 'error') }));
    }

    // --------------------------------------------------------------- imports

    loadImports() {
        this.setState({ isLoadingImports: true });

        mapApi
            .imports()
            .then((imports) => this.setState({ imports, isLoadingImports: false }))
            .catch((error) => this.setState({ isLoadingImports: false, importError: error.message }));
    }

    startImport() {
        const zip = this.state.importZip;

        this.setState({ isStartingImport: true, importError: null });

        mapApi
            .startImport(zip, this.state.includeResidential)
            .then(() => {
                this.setState({ isStartingImport: false, importZip: '' });
                this.loadImports();
            })
            .catch((error) => this.setState({ isStartingImport: false, importError: error.message }));
    }

    openQueue(record) {
        this.setState({ step: 'queue', activeImport: record, queueAnswers: {}, queueReview: null }, () => this.loadQueue('all'));
    }

    /**
     * The queue from its first page, for one tab: everything, or only the rows
     * waiting on a choice or on Kore. A tab left before its page came back
     * keeps that page out.
     */
    loadQueue(tab, then) {
        const { activeImport } = this.state;
        const load = (this.queueLoad = (this.queueLoad || 0) + 1);

        this.setState({
            queueTab: tab,
            isLoadingQueue: true,
            queueRows: [],
            queueRejected: {},
            queueTotal: 0,
            queueCursor: null,
        });

        mapApi
            .importRows(activeImport.id, 'new', null, koreFilter(tab))
            .then((response) => {
                if (load !== this.queueLoad) return;

                this.setState({
                    queueRows: response.data,
                    queueTotal: (response.meta && response.meta.total) || response.data.length,
                    queueCursor: nextCursor(response.meta),
                    queueCounts: (response.meta && response.meta.kore) || null,
                    isLoadingQueue: false,
                }, then);
            })
            .catch((error) => this.setState({ isLoadingQueue: false, toast: toast(error.message, 'error') }));
    }

    /**
     * The next page of the queue, appended to what is already on screen.
     *
     * Guarded on both flags because the scroll sentinel can come back into view
     * while a page is still in flight — the rows it is waiting for are what
     * will push it back down.
     */
    loadMoreQueue() {
        const { activeImport, queueCursor, isLoadingMoreQueue, isLoadingQueue, queueTab } = this.state;

        if (!activeImport || !queueCursor || isLoadingMoreQueue || isLoadingQueue) return;

        this.setState({ isLoadingMoreQueue: true });

        mapApi
            .importRows(activeImport.id, 'new', queueCursor, koreFilter(queueTab))
            .then((response) =>
                this.setState((prev) => ({
                    queueRows: prev.queueRows.concat(response.data),
                    queueTotal: (response.meta && response.meta.total) || prev.queueTotal,
                    queueCursor: nextCursor(response.meta),
                    queueCounts: (response.meta && response.meta.kore) || prev.queueCounts,
                    isLoadingMoreQueue: false,
                }))
            )
            .catch((error) =>
                // The rows already loaded stay usable, so this is a toast
                // rather than an error state that replaces the list.
                this.setState({ isLoadingMoreQueue: false, toast: toast('Could not load more rows. Scroll again to retry.', 'error') })
            );
    }

    /**
     * Approving sends only the ticked rows Kore has cleared or someone answered
     * for; the rest stay queued, and the toast says how many.
     */
    decideQueue(decision) {
        const { activeImport, queueRows, queueRejected, queueAnswers } = this.state;
        const ticked = queueRows.filter((row) => !queueRejected[row.id]);
        const rows = decision === 'approved'
            ? ticked.filter((row) => isReadyToAdd(row, queueAnswers))
            : queueRows.filter((row) => queueRejected[row.id]);
        const staying = decision === 'approved' ? ticked.length - rows.length : 0;

        if (rows.length === 0) return;

        this.setState({ isSaving: true });

        mapApi
            .decideRows(activeImport.id, rows.map((row) => row.id), decision, [], decision === 'approved' ? answersFor(rows, queueAnswers) : [])
            .then(() => {
                const message = rows.length + ' rows ' + decision + (staying > 0 ? ' · ' + staying + ' stay in the queue' : '');

                this.setState({ isSaving: false, step: 'imports', toast: toast(message, 'ok') }, () => this.startToastTimer());
                this.loadImports();
            })
            .catch((error) => this.setState({ isSaving: false, toast: toast(error.message, 'error') }));
    }

    /**
     * The window for the owners Kore was unsure about. From the button it opens
     * on the Needs review tab, so every such row is loaded, not only those the
     * first page of All happened to hold.
     */
    reviewQueue(key) {
        const { queueTab, queueRows, queueAnswers } = this.state;

        if (!key && queueTab !== 'review') {
            this.loadQueue('review', () => this.reviewQueue(null));

            return;
        }

        const groups = reviewGroups(queueRows);

        if (groups.length === 0) return;

        const open = groups.filter((group) => !queueAnswers[group.key]);

        this.setState({ queueReview: { activeKey: key || (open[0] || groups[0]).key, notice: null } });
    }

    /** One owner answered; the window moves on to the next one still open. */
    answerQueue(key, answer) {
        this.setState((prev) => {
            const answers = { ...prev.queueAnswers, [key]: answer };
            const open = reviewGroups(prev.queueRows).filter((group) => !answers[group.key]);

            return {
                queueAnswers: answers,
                queueReview: { ...prev.queueReview, activeKey: open[0] ? open[0].key : key, notice: null },
            };
        });
    }

    /**
     * The owners answered in the window go in now, the rest stay for later. The
     * queue stays open, without the rows just sent: approving runs in the
     * background, so reading the queue back now would still list them.
     */
    confirmQueueReview() {
        const { activeImport, queueRows, queueRejected, queueAnswers, queueReview } = this.state;
        const rows = answeredRows(queueRows, queueRejected, queueAnswers);

        if (rows.length === 0) {
            this.setState({ queueReview: { ...queueReview, notice: 'Choose for at least one owner first.' } });

            return;
        }

        this.setState({ isSaving: true });

        mapApi
            .decideRows(activeImport.id, rows.map((row) => row.id), 'approved', [], answersFor(rows, queueAnswers))
            .then(() => {
                const sent = {};

                rows.forEach((row) => { sent[row.id] = true; });

                this.setState((prev) => ({
                    isSaving: false,
                    queueReview: null,
                    queueRows: prev.queueRows.filter((row) => !sent[row.id]),
                    queueTotal: Math.max(0, prev.queueTotal - rows.length),
                    queueCounts: prev.queueCounts && {
                        ...prev.queueCounts,
                        all: Math.max(0, prev.queueCounts.all - rows.length),
                        review: Math.max(0, prev.queueCounts.review - rows.length),
                    },
                    toast: toast(rows.length + ' rows approved', 'ok'),
                }), () => this.startToastTimer());
            })
            .catch((error) => this.setState({ isSaving: false, toast: toast(error.message, 'error') }));
    }

    /** "Check again": the rows Kore never answered for, sent back to it. */
    recheckQueue() {
        const { activeImport, queueTab } = this.state;

        this.setState({ isSaving: true });

        mapApi
            .recheckKore(activeImport.id)
            .then((result) => {
                this.setState({ isSaving: false, toast: toast(result.queued + ' rows sent to Kore', 'ok') }, () => this.startToastTimer());
                this.loadQueue(queueTab);
            })
            .catch((error) => this.setState({ isSaving: false, toast: toast(error.message, 'error') }));
    }

    renderQueueReview() {
        const { queueReview, queueRows, queueRejected, queueAnswers, isSaving } = this.state;

        if (!queueReview) return null;

        const groups = reviewGroups(queueRows);
        const answered = answeredRows(queueRows, queueRejected, queueAnswers).length;
        const items = groups.map((group) => ({
            key: group.key,
            title: group.parcel.owner_name || 'Owner not named',
            subtitle: plural(group.rows.length, 'property', 'properties') + (group.parcel.city ? ' · ' + group.parcel.city : ''),
            adding: {
                name: group.parcel.owner_name,
                lines: [
                    group.rows.map((row) => row.address || row.apn).join(' · '),
                    group.parcel.owner_address && 'Mail: ' + group.parcel.owner_address,
                ],
            },
            matches: group.kore.matches || [],
            total: group.kore.total || 0,
        }));

        return (
            <LeadMatchesModal
                mode="bulk"
                status="ready"
                items={items}
                activeKey={queueReview.activeKey}
                decisions={queueAnswers}
                total={answered}
                notice={queueReview.notice}
                isSaving={isSaving}
                confirmLabel={'Approve ' + plural(answered, 'property', 'properties')}
                savingLabel="Approving…"
                listLabel="Owners to decide"
                onActivate={(key) => this.setState({ queueReview: { ...queueReview, activeKey: key, notice: null } })}
                onUse={(key, match) => this.answerQueue(key, { koreLeadId: match.lead_id, leadName: match.name })}
                onCreateNew={(key) => this.answerQueue(key, { createNew: true })}
                onConfirm={() => this.confirmQueueReview()}
                onCancel={() => this.setState({ queueReview: null })}
            />
        );
    }

    // ----------------------------------------------------------------- pins

    /** The list filters carried in the URL, in the shape the pins API takes. */
    pinFilters() {
        const query = (this.props.location && this.props.location.query) || {};
        const filters = {};

        FILTER_KEYS.forEach((key) => {
            if (query[key]) filters[key] = String(query[key]).split(',').filter(Boolean);
        });

        return filters;
    }

    hasPinFilters() {
        return Object.keys(this.pinFilters()).length > 0;
    }

    /** Opened from a list filtered by territory — the view the client asked to see darker. */
    isTerritoryView() {
        if (this.territoryView === undefined) {
            const filters = this.pinFilters();

            this.territoryView = TERRITORY_FILTER_KEYS.some((key) => key in filters);
        }

        return this.territoryView;
    }

    loadPins() {
        mapApi
            .pins(this.pinFilters())
            .then((response) => {
                const pins = response;

                this.setState({ pins });
                // Absent when the Maps script did not load; the count in the
                // status bar is still worth showing.
                if (this.pinLayer) this.pinLayer.setPins(pins);
                // Outlines drawn before the pins answered were styled against
                // an empty id set, so they are told about it.
                this.restyleParcels();
            })
            .catch((error) => this.setState({ error: error.message }));
    }

    /**
     * The filtered properties as a set of ids, for tinting parcels. Rebuilt
     * only when the pins change, same reasoning as shapeParcelIds().
     */
    pinIdSet() {
        const { pins } = this.state;

        if (this.pinIdsFor !== pins) {
            this.pinIdsFor = pins;
            this.pinIds = new Set((pins || []).map((pin) => pin.id));
        }

        return this.pinIds;
    }

    /**
     * How many of the filtered properties each legend entry stands for, the
     * client's figures under its key; null until the pins answer.
     */
    pinCounts() {
        const { pins } = this.state;

        if (this.pinCountsFor !== pins) {
            this.pinCountsFor = pins;
            this.pinCountsByKind = pins && pins.reduce((counts, pin) => {
                if (pin.flag) counts[pin.flag] = (counts[pin.flag] || 0) + 1;
                return counts;
            }, { all: pins.length });
        }

        return this.pinCountsByKind;
    }

    // ------------------------------------------------------- category filter

    /**
     * One pick in the category filter. What it makes unreachable goes with it,
     * by the same rule the add form clears by.
     */
    changeCategoryFilter(key, value) {
        const categoryFilter = { ...this.state.categoryFilter, [key]: value };

        if (!value) delete categoryFilter[key];
        fieldsClearedBy(key, value).forEach((dependent) => { delete categoryFilter[dependent]; });

        this.setState({ categoryFilter }, () => this.loadMatches());
    }

    clearCategoryFilter() {
        this.setState({ categoryFilter: {} }, () => this.loadMatches());
    }

    /**
     * The properties the picks match, within whatever list filters the map
     * was opened with. Only the latest request may land: picks change faster
     * than a statewide answer comes back.
     */
    loadMatches() {
        const picked = categoryPinFilters(this.state.categoryFilter);
        const request = (this.matchRequest || 0) + 1;

        this.matchRequest = request;

        if (Object.keys(picked).length === 0) {
            this.showMatches(null);
            return;
        }

        const filters = narrowFilters(this.pinFilters(), picked);

        if (filters === null) {
            this.showMatches([]);
            return;
        }

        this.setState({ isLoadingMatches: true, matchesError: null });

        mapApi
            .pins(filters)
            .then((matches) => {
                if (this.matchRequest === request) this.showMatches(matches);
            })
            .catch((error) => {
                if (this.matchRequest !== request) return;

                this.setState({
                    isLoadingMatches: false,
                    matchesError: error.message,
                    toast: toast('Could not load the matching properties. Try again.', 'error'),
                });
            });
    }

    /**
     * The view stays where the user put it: the client asked for the results
     * in the area they zoomed into, not a jump away from it. Show all frames
     * them on request.
     */
    showMatches(matches) {
        this.setState({ matches, isLoadingMatches: false, matchesError: null }, () => {
            if (this.pinLayer) this.pinLayer.setMatches(matches === null ? null : this.matchIdSet());
            this.restyleParcels();
        });
    }

    /**
     * Frame every match at once — the search's fit, for many properties. The
     * card's margin is kept only while the card is open to cover the map.
     */
    fitToMatches() {
        this.frameMatches(this.state.matches);
    }

    frameMatches(matches) {
        const { step } = this.state;

        if (!this.map || !matches || matches.length === 0) return;

        const bounds = new google.maps.LatLngBounds();

        matches.forEach((pin) => bounds.extend({ lat: pin.lat, lng: pin.lng }));

        const span = bounds.toSpan();

        if (span.lat() < MATCH_CLOSE_SPAN_DEGREES && span.lng() < MATCH_CLOSE_SPAN_DEGREES) {
            this.map.setCenter(bounds.getCenter());
            this.map.setZoom(MATCH_CLOSE_ZOOM);
            return;
        }

        this.map.fitBounds(bounds, { ...SEARCH_FIT_PADDING, left: step ? SEARCH_FIT_PADDING.left : SEARCH_FIT_PADDING.right });
    }

    /**
     * A match clicked from afar: the map comes down onto it at once, its card
     * opens, and the view settles on its parcel once the parcel feed answers
     * — which took over eight seconds here, far too long to hold a click.
     */
    zoomOntoMatch(pin) {
        this.map.setCenter({ lat: pin.lat, lng: pin.lng });
        this.map.setZoom(MATCH_CLOSE_ZOOM);

        this.openProperty(pin.id, pin).then((result) => {
            if (result && result.parcel && parcelBounds(result.parcel)) this.fitToParcel(result.parcel);
        });
    }

    /**
     * The drawn parcels the matches or the pins stand on, rebuilt when they or
     * the drawn outlines change — the same caching as shapeParcelIds().
     *
     * @param {'matches'|'pins'} list
     */
    parcelsStoodOn(list) {
        const points = this.state[list];
        const outlines = this.parcelLayer.getOutlines();
        const cached = (this.stoodOn || {})[list];

        if (!cached || cached.points !== points || cached.outlines !== outlines) {
            this.stoodOn = {
                ...this.stoodOn,
                [list]: { points, outlines, parcelIds: points ? this.parcelLayer.parcelsUnder(points) : new Set() },
            };
        }

        return this.stoodOn[list].parcelIds;
    }

    /**
     * Either the parcel's own property is on the list, or one on it stands on
     * the parcel: a parcel carries one property id, and some carry none at all.
     */
    isParcelOf(list, ids, parcelId, outline) {
        return Boolean(outline && outline.property_id && ids.has(outline.property_id))
            || this.parcelsStoodOn(list).has(parcelId);
    }

    isMatchParcel(parcelId, outline) {
        return this.isParcelOf('matches', this.matchIdSet(), parcelId, outline);
    }

    /** Same caching as pinIdSet(), for the filter's matches. */
    matchIdSet() {
        const { matches } = this.state;

        if (this.matchIdsFor !== matches) {
            this.matchIdsFor = matches;
            this.matchIds = new Set((matches || []).map((pin) => pin.id));
        }

        return this.matchIds;
    }

    // ---------------------------------------------------------------- panel

    openPropertyNotes() {
        const { parcel, existing } = this.state.selection;

        this.setState({
            notes: {
                owner: { type: 'property', id: existing.id },
                title: existing.name || parcel.address || 'Property',
                subtitle: 'Property notes',
            },
        });
    }

    openInvestorNotes(investor) {
        this.setState({
            notes: {
                owner: { type: 'investor', id: investor.id },
                title: investor.name,
                subtitle: 'Investor notes',
                links: [
                    { label: 'iCLIMB', href: '/investor/' + investor.id, title: 'Open ' + investor.name + ' in iCLIMB' },
                    investor.crm_lead_id && { label: 'Kore', href: crmLeadHref(investor.crm_lead_id), title: 'Open the lead in Kore' },
                ].filter(Boolean),
            },
        });
    }

    /** The count on the header's Notes button, kept to what the window last saved. */
    setPropertyNotesCount(propertyId, count) {
        this.setState((prev) => {
            const { selection } = prev;

            if (!selection || !selection.property || String(selection.property.id) !== String(propertyId)) return null;

            return { selection: { ...selection, property: { ...selection.property, notes_count: count } } };
        });
    }

    closePanel() {
        if (this.shapeTool) this.shapeTool.clear();
        this.setState(
            { step: null, selection: null, shapeType: null, shapeResults: [], excluded: {}, tool: null },
            () => this.restyleParcels()
        );
    }

    goBack() {
        const { step } = this.state;

        this.setState({ step: step === 'queue' || step === 'log' ? 'imports' : 'summary' });
    }

    startToastTimer() {
        clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => this.setState({ toast: null }), TOAST_MS);
    }

    // --------------------------------------------------------------- render

    /**
     * One row of the layers menu: icon, name, what it does, and its switch.
     */
    renderLayer(icon, name, note, isOn, onToggle) {
        return (
            <button type="button" className="ParcelMap-layer" onClick={onToggle} role="switch" aria-checked={isOn}>
                {icon}
                <span className="ParcelMap-layer-text">
                    <span className="ParcelMap-layer-name">{name}</span>
                    <span className="ParcelMap-layer-note">{note}</span>
                </span>
                <span className={'ParcelMap-switch' + (isOn ? ' ParcelMap-switch--on' : '')}>
                    <span className="ParcelMap-switch-knob" />
                </span>
            </button>
        );
    }

    /**
     * The legend on one row while the bar has room for it, the flags folded
     * behind a button when it has not. Room is what the spacer and the search
     * box hold beyond its own width: they give way first, then Category.
     */
    fitLegend() {
        if (!this.legendNode || !this.searchBoxNode || !this.spacerNode) return;

        const search = this.searchBoxNode.getBoundingClientRect().width;
        const room = this.spacerNode.getBoundingClientRect().width + search - parseFloat(getComputedStyle(this.searchBoxNode).flexBasis);
        const legend = this.legendNode.getBoundingClientRect().width;

        if (!this.state.legendCompact) {
            this.fullLegendWidth = legend;
            if (room < 0) this.setState({ legendCompact: true });
            return;
        }

        if (room >= this.fullLegendWidth - legend) this.setState({ legendCompact: false, isPinKeyOpen: false });
    }

    renderLegend() {
        const { legendCompact, isPinKeyOpen } = this.state;
        const counts = this.pinCounts();
        const count = (kind) => (counts ? (counts[kind] || 0).toLocaleString() : '…');
        const swatch = (bucket) => <span key={bucket} className={'ParcelMap-legend-swatch ParcelMap-legend-swatch--' + bucket} />;
        const item = (bucket, label, figure) => (
            <span key={bucket} className="ParcelMap-legend-item">
                {swatch(bucket)}
                <span className="ParcelMap-legend-label">{label}</span>
                <span className="ParcelMap-legend-count">{figure}</span>
            </span>
        );

        return (
            <div className={'ParcelMap-legend' + (legendCompact ? ' ParcelMap-legend--compact' : '')} ref={(node) => { this.legendNode = node; }}>
                {item(this.isTerritoryView() ? 'territory' : 'iclimb', 'In iCLIMB', count('all'))}

                {!legendCompact && FLAG_LEGEND.map(({ bucket, label }) => item(bucket, label, count(bucket)))}

                {legendCompact && (
                    <div className="ParcelMap-pinkey" ref={(node) => { this.pinKeyNode = node; }}>
                        <button
                            type="button"
                            className={'ParcelMap-pinkey-button' + (isPinKeyOpen ? ' ParcelMap-pinkey-button--open' : '')}
                            onClick={() => this.setState((prev) => ({ isPinKeyOpen: !prev.isPinKeyOpen }))}
                            aria-expanded={isPinKeyOpen}
                        >
                            <span className="ParcelMap-pinkey-dots">
                                {FLAG_LEGEND.map(({ bucket }) => swatch(bucket))}
                            </span>
                            Status
                            <span className={'ParcelMap-layers-caret' + (isPinKeyOpen ? ' ParcelMap-layers-caret--open' : '')}>
                                <CaretDownIcon />
                            </span>
                        </button>

                        {isPinKeyOpen && (
                            <div className="ParcelMap-pinkey-menu" role="menu">
                                <div className="ParcelMap-layers-heading">Pin colors</div>

                                {FLAG_LEGEND.map(({ bucket, label, note }) => (
                                    <div key={bucket} className="ParcelMap-pinkey-row">
                                        <img className="ParcelMap-pinkey-pin" src={flagPinUrl(bucket)} alt="" />
                                        <span className="ParcelMap-layer-text">
                                            <span className="ParcelMap-layer-name">{label}</span>
                                            <span className="ParcelMap-layer-note">{note}</span>
                                        </span>
                                        <span className="ParcelMap-pinkey-count">{count(bucket)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    renderTopBar() {
        const { showParcels, includeResidential, isLayersOpen, tool, imports } = this.state;
        const awaiting = imports.filter((record) => record.pending_count > 0).length;
        const layersOn = (showParcels ? 1 : 0) + (includeResidential ? 1 : 0);

        return (
            <div className="ParcelMap-topbar" ref={(node) => { this.topbarNode = node; }}>
                <Link className="ParcelMap-listview" to="/list">
                    <ChevronLeftIcon />
                    List view
                </Link>

                {this.renderLegend()}

                <div className="ParcelMap-search" ref={(node) => { this.searchBoxNode = node; }}>
                    {this.state.isSearching ? <span className="ParcelMap-search-spinner" /> : <SearchIcon />}
                    <input
                        className="ParcelMap-search-input"
                        placeholder="Search an address"
                        ref={(node) => { this.searchNode = node; }}
                    />
                    <span className="ParcelMap-search-key">⌘K</span>
                </div>

                <CategoryFilter
                    values={this.state.categoryFilter}
                    onChange={(key, value) => this.changeCategoryFilter(key, value)}
                    onClear={() => this.clearCategoryFilter()}
                    onShowAll={() => this.fitToMatches()}
                    matchCount={this.state.matches === null ? null : this.state.matches.length}
                    isLoading={this.state.isLoadingMatches}
                    error={this.state.matchesError}
                />

                <div className="ParcelMap-spacer" ref={(node) => { this.spacerNode = node; }} />

                <div className="ParcelMap-actions">
                    <div className="ParcelMap-layers" ref={(node) => { this.layersNode = node; }}>
                        <button
                            type="button"
                            className={'ParcelMap-layers-button' + (isLayersOpen ? ' ParcelMap-layers-button--open' : '')}
                            onClick={() => this.setState({ isLayersOpen: !isLayersOpen })}
                            aria-haspopup="menu"
                            aria-expanded={isLayersOpen}
                        >
                            <LayersIcon />
                            Layers
                            <span className="ParcelMap-layers-count">{layersOn}</span>
                            <span className={'ParcelMap-layers-caret' + (isLayersOpen ? ' ParcelMap-layers-caret--open' : '')}>
                                <CaretDownIcon />
                            </span>
                        </button>

                        {isLayersOpen && (
                            <div className="ParcelMap-layers-menu" role="menu">
                                <div className="ParcelMap-layers-heading">Map layers</div>

                                {this.renderLayer(
                                    <ParcelLinesIcon />,
                                    'Parcel lines',
                                    'Boundaries and lot outlines',
                                    showParcels,
                                    () => this.toggleParcels(),
                                )}

                                {this.renderLayer(
                                    <HouseIcon />,
                                    'Houses',
                                    'Residential building footprints',
                                    includeResidential,
                                    () => this.toggleResidential(),
                                )}
                            </div>
                        )}
                    </div>

                    <div className="ParcelMap-actions-divider" />

                    <div className="ParcelMap-segment">
                        <button
                            type="button"
                            className={'ParcelMap-tool' + (tool === null ? ' ParcelMap-tool--on' : '')}
                            onClick={() => this.pickTool(null)}
                            title="Pan and select"
                            aria-label="Pan and select"
                        >
                            <CursorIcon />
                        </button>
                        <button
                            type="button"
                            className={'ParcelMap-tool' + (tool === 'box' ? ' ParcelMap-tool--on' : '')}
                            onClick={() => this.pickTool('box')}
                            title="Select an area with a box"
                            aria-label="Select an area with a box"
                        >
                            <BoxIcon />
                        </button>
                        <button
                            type="button"
                            className={'ParcelMap-tool' + (tool === 'radius' ? ' ParcelMap-tool--on' : '')}
                            onClick={() => this.pickTool('radius')}
                            title="Select within a radius"
                            aria-label="Select within a radius"
                        >
                            <RadiusIcon />
                        </button>
                    </div>

                    <div className="ParcelMap-actions-divider" />

                    <button
                        type="button"
                        className="ParcelMap-imports"
                        onClick={() => this.setState({ step: 'imports', isLayersOpen: false })}
                    >
                        <ImportIcon />
                        Imports
                        {awaiting > 0 && <span className="ParcelMap-imports-badge">{awaiting}</span>}
                    </button>
                </div>
            </div>
        );
    }

    /**
     * The one line above the map.
     *
     * The parcel feed's own failures read as stack traces — a cURL timeout
     * arrives as a paragraph with the whole upstream URL in it — so they are
     * reported as something a user can act on. The detail still goes to the
     * console for whoever is debugging.
     */
    hintText() {
        const { tool, parcelStatus, showParcels } = this.state;

        if (tool === 'box') return { text: 'Drag to draw a box, or click to place one at the set size' };
        if (tool === 'radius') return { text: 'Click the centre, or drag out to the radius you want' };
        if (!showParcels) return null;
        if (parcelStatus.status === 'zoom') return { text: 'Zoom in to load parcel lines' };
        if (parcelStatus.status === 'dense') return { text: 'Too many parcels here — zoom in to load them' };

        if (parcelStatus.status === 'error') {
            return { text: 'Parcel lines are unavailable right now. Try again in a moment.', isError: true };
        }

        return null;
    }

    /**
     * The parcel half of the status bar.
     *
     * While loading the number is replaced by a shimmer rather than a stale or
     * zero count, with the words kept so the pill does not change width and
     * shuffle the one beside it.
     */
    renderParcelStat() {
        const { showParcels, isLoadingParcels } = this.state;

        if (showParcels && isLoadingParcels) {
            return (
                <div className="ParcelMap-stat">
                    <span className="ParcelMap-spinner" />
                    <span className="ParcelMap-shimmer" />
                    <span>parcels in view</span>
                </div>
            );
        }

        return <div className="ParcelMap-stat">{this.parcelCountLabel()}</div>;
    }

    renderTerritoryStat() {
        const { territory } = this.state;

        if (!territory) return null;

        if (territory.status === 'loading') return <div className="ParcelMap-stat">Loading territory outline…</div>;
        if (territory.status === 'error') return <div className="ParcelMap-stat">Territory outline unavailable</div>;

        const { missing } = territory;

        return (
            <React.Fragment>
                <div className="ParcelMap-stat">{'Territory outline · ' + territory.zips.toLocaleString() + ' ZIPs'}</div>
                {missing.length > 0 && (
                    <div className="ParcelMap-stat" title={missing.join(', ')}>
                        {'No shape for ' + missing.slice(0, MISSING_ZIPS_LISTED).join(', ')
                            + (missing.length > MISSING_ZIPS_LISTED ? ' and ' + (missing.length - MISSING_ZIPS_LISTED) + ' more' : '')}
                    </div>
                )}
            </React.Fragment>
        );
    }

    parcelCountLabel() {
        const { parcelStatus, showParcels } = this.state;

        if (!showParcels) return 'Parcel lines off';
        if (parcelStatus.status === 'zoom') return 'Parcels hidden at this zoom';
        if (parcelStatus.status === 'dense') return 'Parcels hidden at this zoom';
        if (parcelStatus.status === 'loading') return 'Loading parcels…';
        // "0 parcels in view" reads as an empty area rather than a failure.
        if (parcelStatus.status === 'error') return 'Parcel lines unavailable';

        return parcelStatus.count.toLocaleString() + ' parcels in view';
    }

    panelHeading() {
        const { step, selection, shapeType, shapeResults, activeImport } = this.state;

        if (step === 'selection') {
            return {
                title: shapeType === 'box' ? 'Box selection' : 'Radius selection',
                subtitle: shapeResults.length + ' parcels captured',
            };
        }

        if (step === 'imports') return { title: 'ZIP imports', subtitle: 'Background jobs and approval queue' };
        if (step === 'queue') return { title: 'Approval queue', subtitle: activeImport && 'ZIP ' + activeImport.zip };
        if (step === 'log') return { title: 'Import log', subtitle: activeImport && 'ZIP ' + activeImport.zip };

        if (!selection) return { title: 'Parcel', subtitle: null };

        const { parcel, existing } = selection;
        // existing rather than the property, which arrives a request later:
        // the heading would otherwise open on the address and rearrange itself
        // once the name landed.
        const name = existing && existing.name;
        const title = name || parcel.address || 'Parcel';
        let place = [parcel.city, parcel.state].filter(Boolean).join(', ') + (parcel.zip ? ' ' + parcel.zip : '');

        // A parcel nobody has filed yet knows its market and nothing more of
        // its territory, and one line on its own read as a heading with no
        // content under it — so it rides along with the town.
        if (!existing && selection.territory && selection.territory.market) {
            place = [place, selection.territory.market].filter(Boolean).join(' · ');
        }

        if (step === 'add') return { title, subtitle: 'Add to iCLIMB' };
        if (step === 'update') return { title, subtitle: 'Update from LightBox' };

        // The address earns a line of its own only under a name. Without one
        // the title already is the address, and repeating it read as a bug.
        return { title, subtitle: [name ? parcel.address : null, place] };
    }

    renderStep() {
        const {
            step, selection, isLoadingSelection, form, isSaving,
            diffRows, diffRejected, isLoadingDiff, diffError,
            shapeType, size, shapeResults, isLoadingShape, selTab, excluded,
            imports, isLoadingImports, importZip, isStartingImport, importError, activeImport,
            queueRows, queueRejected, isLoadingQueue, queueTotal, queueCursor, isLoadingMoreQueue,
            queueTab, queueCounts, queueAnswers,
        } = this.state;

        if (step === 'summary' || step === 'add' || step === 'update') {
            if (isLoadingSelection && !selection) return <div className="SidePanel-loading">Loading parcel…</div>;
            if (!selection) return null;
        }

        if (step === 'summary') {
            return (
                <SummaryStep
                    selection={selection}
                    isSaving={isSaving}
                    owner={this.state.owner}
                    onOwnerChange={(owner) => this.pickOwner(owner)}
                    foundInvestors={this.state.foundInvestors}
                    onFoundInvestors={(foundInvestors) => this.setState({ foundInvestors }, () => this.syncHandoff())}
                    needOwner={this.state.needOwner}
                    onNeedOwnerChange={(needOwner) => this.changeNeedOwner(needOwner)}
                    onSaveOwner={() => this.withLeadCheck('saveOwner')}
                    isSavingOwner={this.state.isSavingOwner}
                    ownerSavedAt={this.state.ownerSavedAt}
                    koreWait={this.state.leadWaiting}
                    onInvestorNotes={(investor) => this.openInvestorNotes(investor)}
                    onPrimary={() =>
                        selection.existing
                            ? this.openDiff()
                            // Wanting to add is when Kore is first asked about the owner.
                            : this.setState({ step: 'add', form: { ...selection.suggested } }, () => this.checkLeads())}
                    onSecondary={() =>
                        selection.existing
                            ? window.open('/property/' + selection.existing.id, '_blank')
                            : this.withLeadCheck('fullForm')}
                />
            );
        }

        if (step === 'add') {
            return (
                <AddFormStep
                    values={form || {}}
                    isSaving={isSaving}
                    isCheckingKore={this.state.leadWaiting === 'add'}
                    onChange={(key, value) => this.onFormField(key, value)}
                    onSave={() => this.withLeadCheck('add')}
                    onCancel={() => this.setState({ step: 'summary' })}
                    parcel={selection.parcel}
                    candidates={selection.owner_candidates}
                    owner={this.state.owner}
                    onOwnerChange={(owner) => this.pickOwner(owner)}
                    foundInvestors={this.state.foundInvestors}
                    onFoundInvestors={(foundInvestors) => this.setState({ foundInvestors }, () => this.syncHandoff())}
                    needOwner={this.state.needOwner}
                    onNeedOwnerChange={(needOwner) => this.setState({ needOwner, leadWaiting: null }, () => this.syncHandoff())}
                />
            );
        }

        if (step === 'update') {
            return (
                <UpdateStep
                    rows={diffRows}
                    isLoading={isLoadingDiff}
                    error={diffError}
                    rejected={diffRejected}
                    isSaving={isSaving}
                    onToggle={(key) =>
                        this.setState((prev) => ({ diffRejected: { ...prev.diffRejected, [key]: !prev.diffRejected[key] } }))}
                    onAcceptAll={() => this.setState({ diffRejected: {} })}
                    onApply={() => this.applyDiff()}
                />
            );
        }

        if (step === 'selection') {
            const shown = shapeResults.filter((result) =>
                selTab === 'new' ? !result.existing_property : result.existing_property);
            const kept = shown.filter((result) => !excluded[result.parcel.parcel_id]);

            return (
                <SelectionStep
                    shapeType={shapeType}
                    size={size}
                    results={shapeResults}
                    isLoading={isLoadingShape}
                    isSaving={isSaving}
                    koreCheck={this.state.bulkCheck}
                    tab={selTab}
                    excluded={excluded}
                    onSize={(key, value) => this.onSizeTyped(key, value)}
                    onPreset={(miles) => this.onPreset(miles)}
                    onTab={(selTab) => this.setState({ selTab })}
                    onToggle={(parcelId) =>
                        this.setState(
                            (prev) => ({ excluded: { ...prev.excluded, [parcelId]: !prev.excluded[parcelId] } }),
                            () => this.restyleParcels()
                        )}
                    onToggleAll={() => this.toggleAllInShape(shown, kept)}
                    onHoverRow={(hoverRowId) => this.setHover('hoverRowId', hoverRowId)}
                    onBulk={() => (selTab === 'new' ? this.startBulkAdd(kept) : this.bulkUpdate(kept))}
                    onClear={() => this.clearShape()}
                />
            );
        }

        if (step === 'imports') {
            return (
                <ImportsStep
                    imports={imports}
                    isLoading={isLoadingImports}
                    zip={importZip}
                    isStarting={isStartingImport}
                    error={importError}
                    onZip={(importZip) => this.setState({ importZip })}
                    onStart={() => this.startImport()}
                    onQueue={(record) => this.openQueue(record)}
                    onLog={(record) => this.setState({ step: 'log', activeImport: record })}
                />
            );
        }

        if (step === 'queue') {
            return (
                <QueueStep
                    rows={queueRows}
                    total={queueTotal}
                    hasMore={Boolean(queueCursor)}
                    isLoading={isLoadingQueue}
                    isLoadingMore={isLoadingMoreQueue}
                    rejected={queueRejected}
                    isSaving={isSaving}
                    tab={queueTab}
                    counts={queueCounts}
                    answers={queueAnswers}
                    onTab={(tab) => this.loadQueue(tab)}
                    onToggle={(id) =>
                        this.setState((prev) => ({ queueRejected: { ...prev.queueRejected, [id]: !prev.queueRejected[id] } }))}
                    onToggleAll={() => this.toggleAllInQueue()}
                    onLoadMore={() => this.loadMoreQueue()}
                    onApprove={() => this.decideQueue('approved')}
                    onReject={() => this.decideQueue('rejected')}
                    onReview={(key) => this.reviewQueue(key)}
                    onRecheck={() => this.recheckQueue()}
                />
            );
        }

        if (step === 'log' && activeImport) return <LogStep record={activeImport} />;

        return null;
    }

    onSizeTyped(key, value) {
        const miles = Math.max(0.02, parseFloat(value) || 0);

        this.setState((prev) => ({ size: { ...prev.size, [key]: miles } }));
        this.shapeTool.resizeTo({ [key]: miles });
    }

    onPreset(miles) {
        const size = this.state.shapeType === 'box'
            ? { widthMiles: miles, heightMiles: miles }
            : { radiusMiles: miles };

        this.setState((prev) => ({ size: { ...prev.size, ...size } }));
        this.shapeTool.resizeTo(size);
    }

    toggleAllInShape(shown, kept) {
        const excluded = { ...this.state.excluded };
        const dropAll = kept.length === shown.length;

        shown.forEach((result) => {
            if (dropAll) {
                excluded[result.parcel.parcel_id] = true;
            } else {
                delete excluded[result.parcel.parcel_id];
            }
        });

        this.setState({ excluded }, () => this.restyleParcels());
    }

    toggleAllInQueue() {
        const { queueRows, queueRejected } = this.state;
        const approved = queueRows.filter((row) => !queueRejected[row.id]);

        if (approved.length === queueRows.length) {
            const rejected = {};
            queueRows.forEach((row) => { rejected[row.id] = true; });
            this.setState({ queueRejected: rejected });
        } else {
            this.setState({ queueRejected: {} });
        }
    }

    render() {
        const { pins, step, zoom, toast: message, error, showParcels, parcelStatus, isLoadingParcels } = this.state;
        const heading = this.panelHeading();
        const hint = this.hintText();
        // Not while a filter is on: zoomed out is where its matches are read,
        // and the dimming buried them. Nor over the client's territory, which he wants clear.
        const dim = showParcels && (parcelStatus.status === 'zoom' || parcelStatus.status === 'dense')
            && this.state.matches === null && !this.isTerritoryView();

        return (
            <div className={'ParcelMap' + (this.isTerritoryView() ? ' ParcelMap--territory' : '')}>
                <div className="ParcelMap-canvas" ref={(node) => { this.mapNode = node; }} />
                <div className={'ParcelMap-wash' + (dim ? ' ParcelMap-wash--dim' : '')} />

                {this.renderTopBar()}

                {showParcels && isLoadingParcels && (
                    <div className="ParcelMap-progress">
                        <div className="ParcelMap-progress-bar" />
                    </div>
                )}

                <div className="ParcelMap-hintRow">
                    {hint && (
                        <div className={'ParcelMap-hint' + (hint.isError ? ' ParcelMap-hint--error' : '')}>
                            {hint.text}
                        </div>
                    )}
                </div>

                {step && (
                    <SidePanel
                        title={heading.title}
                        subtitle={heading.subtitle}
                        actions={this.renderPanelActions()}
                        canBack={['add', 'update', 'queue', 'log'].indexOf(step) >= 0}
                        onBack={() => this.goBack()}
                        onClose={() => this.closePanel()}
                    >
                        {this.renderStep()}
                    </SidePanel>
                )}

                <div className="ParcelMap-statusbar">
                    <div className="ParcelMap-stat">
                        {error
                            ? error
                            : pins === null
                                ? 'Loading properties…'
                                : pins.length.toLocaleString()
                                    + (this.hasPinFilters() ? ' properties in your list filters' : ' properties in iCLIMB')}
                        {this.state.matches !== null && ' · showing ' + this.state.matches.length.toLocaleString() + ' that match'}
                    </div>
                    {this.renderParcelStat()}
                    {this.renderTerritoryStat()}
                </div>

                <div className="ParcelMap-controls">
                    <div className="ParcelMap-scale">{scaleLabel(zoom, this.state.latitude)}</div>
                    <div className="ParcelMap-zoom">
                        <button type="button" className="ParcelMap-zoom-button" onClick={() => this.setZoom(1)} aria-label="Zoom in">+</button>
                        <div className="ParcelMap-zoom-level">z{zoom}</div>
                        <button type="button" className="ParcelMap-zoom-button" onClick={() => this.setZoom(-1)} aria-label="Zoom out">−</button>
                    </div>
                </div>

                <div className="ParcelMap-toastRow">
                    {message && (
                        <div className={'ParcelMap-toast' + (message.kind === 'error' ? ' ParcelMap-toast--error' : '')}>
                            <span className="ParcelMap-toast-text">{message.text}</span>
                            <div className="ParcelMap-toast-actions">
                                {message.undoId && (
                                    <button type="button" className="ParcelMap-toast-undo" onClick={() => this.undoAdd(message.undoId)}>
                                        Undo
                                    </button>
                                )}
                                <button type="button" className="ParcelMap-toast-dismiss" onClick={() => this.setState({ toast: null }, dropRestOfDoubleClick)}>
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {this.renderLeadModal()}
                {this.renderQueueReview()}
                {this.renderNotes()}
            </div>
        );
    }
}

/**
 * The list filters with the category picks laid over them. A column both
 * name keeps only the values both accept; none left means nothing can match.
 *
 * @returns {object|null} null when the two cannot both hold.
 */
function narrowFilters(listFilters, picked) {
    const filters = { ...listFilters };

    for (const key of Object.keys(picked)) {
        const values = filters[key] ? picked[key].filter((value) => filters[key].indexOf(value) >= 0) : picked[key];

        if (values.length === 0) return null;

        filters[key] = values;
    }

    return filters;
}

function toast(text, kind, undoId) {
    return { text, kind: kind || 'ok', undoId: undoId || null };
}

/**
 * The box one parcel's boundary occupies, or null when it carries no geometry
 * at all and there is nothing to frame.
 */
function parcelBounds(parcel) {
    const paths = wktToPaths(parcel.boundary_wkt);

    if (paths.length === 0) {
        if (!parcel.latitude || !parcel.longitude) return null;

        const point = new google.maps.LatLng(parcel.latitude, parcel.longitude);

        return new google.maps.LatLngBounds(point, point);
    }

    const bounds = new google.maps.LatLngBounds();

    paths.forEach((path) => path.forEach((point) => bounds.extend(point)));

    return bounds;
}

/** Where the next page of the approval queue resumes, or null at the end. */
function nextCursor(meta) {
    if (!meta || !meta.next_after_id) return null;

    return { afterAddress: meta.next_after_address, afterId: meta.next_after_id };
}

/** The queue tab as the rows endpoint filters it; All is no filter. */
function koreFilter(tab) {
    return tab === 'all' ? null : tab;
}

/** Ticked rows Kore was unsure about whose owner has been answered for. */
function answeredRows(rows, rejected, answers) {
    return rows.filter((row) => !rejected[row.id] && row.kore && row.kore.status === 'review' && answers[ownerKey(row)]);
}

/** Each answered row's answer, in the shape the decide endpoint takes. */
function answersFor(rows, answers) {
    return answeredRows(rows, {}, answers).map((row) => {
        const answer = answers[ownerKey(row)];

        return answer.createNew ? { row_id: row.id, create_new: true } : { row_id: row.id, kore_lead_id: answer.koreLeadId };
    });
}

function plural(count, one, many) {
    return count + ' ' + (count === 1 ? one : many);
}

/**
 * A parcel lookup that failed, said in one line.
 *
 * The parcel feed reports its own faults as a paragraph of vendor JSON. Put
 * straight into a toast that filled the width of the window and told the user
 * nothing, so only our own coded answers are shown verbatim.
 */
function lookupFailed(error, noParcelText) {
    if (error.code === 'NO_PARCEL') return noParcelText;

    console.warn('Parcel lookup failed:', error.message);

    return 'Could not read the parcel here. Try again in a moment.';
}

/** Columns the form shows with thousands separators, which MySQL will not take. */
const NUMERIC_FIELDS = ['SQFT', 'ACRES', 'ASSESSED_VALUE', 'YEAR_BUILT'];

/** Strips the display formatting back off before the values are saved. */
function numeric(form) {
    const values = { ...form };

    NUMERIC_FIELDS.forEach((key) => {
        if (typeof values[key] === 'string') {
            const clean = values[key].replace(/[^\d.]/g, '');

            values[key] = clean === '' ? null : clean;
        }
    });

    return values;
}

/** Enough of a parcel shape to render the summary when the lookup found none. */
function parcelFromProperty(property) {
    return {
        parcel_id: property.lightbox_parcel_id,
        apn: property.apn,
        address: property.address,
        city: property.city,
        state: property.state,
        zip: property.zip,
        county: property.county,
        year_built: property.year_built,
        building_sqft: property.sqft,
        lot_acres: property.acres,
        assessed_value: property.assessed_value,
        land_use: null,
        legal_description: null,
        owner_name: null,
    };
}
